import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WriteOnly, MaskedValue } from '../components/ViewerGuard';
import { targetsApi, projectsApi, pipelineApi, exportApi, briefingApi, researchApi, formatApiError, handleApiError } from '../lib/api';
import { toast } from 'sonner';
import { pollJob } from '../lib/pollJob';
import { firstNonEmpty } from '../lib/empty';
import usePageTitle from '../hooks/usePageTitle';
import { useTargetSync } from '../contexts/TargetSyncContext';
import {
  ArrowLeft, Building, MapPin, ExternalLink, Users, Zap, Target, Star,
  ChevronDown, ChevronUp, Plus, Check, Loader2, Calendar, User, Leaf,
  AlertTriangle, PlayCircle, ShieldCheck, Package, Monitor, DollarSign,
  Award, TrendingUp, BookOpen, Clapperboard, BarChart3, Shield,
  Upload, X, Eye, FileText, Trash2, Pencil, Mail, Save, Globe, ArrowRight,
  Phone, Youtube, Instagram, Twitter, File as FileIcon, Image, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import BriefingModal from '../components/BriefingModal';

const EmptyPlaceholder = () => (
  <p className="text-[#A3B5C7]/40 text-sm italic" data-testid="empty-placeholder">No data yet — add via Import Context or edit manually</p>
);

// Guard against LLM returning strings where arrays are expected
const safeArray = (v) => Array.isArray(v) ? v : [];

const TargetDetailPage = () => {
  const { id } = useParams();
  usePageTitle('Target');
  const navigate = useNavigate();
  const { syncTarget } = useTargetSync();
  const [target, setTarget] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    mandate: true, contacts: true, pitch: true, relevance: true,
    video_evidence: true, sustainability: true, greenwash: false,
    product_integration: true, screening: false, philanthropic: false,
    video_content: true, current_activity: true, bd_angles: true,
    background: true, mission_alignment: true, engagement: true, evidence: true,
    red_flag: false, film_details: true, key_people: true, financing: true,
    distribution: true, impact: true, awards: true, comparables: true,
    confidence: true, pitch_hooks: true,
  });
  const [addingToPipeline, setAddingToPipeline] = useState(false);
  const [pipelineSuccess, setPipelineSuccess] = useState(false);
  // Context Import state
  const [contextOpen, setContextOpen] = useState(false);
  const [contextText, setContextText] = useState('');
  const [contextFocus, setContextFocus] = useState('all');
  const [contextContentType, setContextContentType] = useState('other');
  const [contextParsing, setContextParsing] = useState(false);
  const [contextPreview, setContextPreview] = useState(null);
  const [contextSelected, setContextSelected] = useState({});
  const [contextApplying, setContextApplying] = useState(false);
  const [contextApplied, setContextApplied] = useState(false);

  // File upload for context import
  const [ctxFile, setCtxFile] = useState(null);
  const [ctxFileExtracting, setCtxFileExtracting] = useState(false);
  const [ctxFileText, setCtxFileText] = useState('');
  const [ctxFileError, setCtxFileError] = useState('');
  const ctxFileInputRef = useRef(null);
  const previewRef = React.useRef(null);
  // Import log
  const [importLog, setImportLog] = useState([]);
  const [importLogOpen, setImportLogOpen] = useState(false);
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);
  // Contact editing state
  const [editingContact, setEditingContact] = useState(null);
  const [contactDraft, setContactDraft] = useState({});
  const [savingContact, setSavingContact] = useState(false);
  // Contact info panel state
  const [editingInfoField, setEditingInfoField] = useState(null);
  const [infoDraft, setInfoDraft] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  // Scroll ref for context import
  const contextSectionRef = React.useRef(null);
  // Inline section editing state
  const [editingSection, setEditingSection] = useState(null);
  const [sectionDraft, setSectionDraft] = useState('');
  const [savingSection, setSavingSection] = useState(false);
  const sectionEditRef = useRef(null);
  // Owner options + pipeline membership
  const [ownerOptions, setOwnerOptions] = useState(['Unassigned']);
  const [inPipeline, setInPipeline] = useState(false);
  const [pipelineRecordId, setPipelineRecordId] = useState(null);

  // Manual override state (relevance score / location / reputational verdict+reasoning)
  const [overrideEditing, setOverrideEditing] = useState(null); // 'relevance' | 'location' | 'reputational'
  const [overrideDraft, setOverrideDraft] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const [showOriginal, setShowOriginal] = useState({});
  // Owner state
  const [updatingOwner, setUpdatingOwner] = useState(false);
  // Delta scan state
  const [deltaScanRunning, setDeltaScanRunning] = useState(false);
  const [deltaScanProgress, setDeltaScanProgress] = useState('');
  const [deltaScanResult, setDeltaScanResult] = useState(null);
  const [reResearching, setReResearching] = useState(false);

  const parsedResearch = useMemo(() => {
    if (!target?.research_raw) return null;
    try {
      const jsonStart = target.research_raw.indexOf('{');
      const jsonEnd = target.research_raw.lastIndexOf('}') + 1;
      if (jsonStart !== -1 && jsonEnd > jsonStart) {
        return JSON.parse(target.research_raw.substring(jsonStart, jsonEnd));
      }
    } catch (e) {
      console.error('Failed to parse target.research_raw JSON:', e);
    }
    return null;
  }, [target?.research_raw]);

  // A card "predates the current schema" if it has research data but none of
  // the new Part C org-card keys — such cards render via fallbacks and benefit
  // from a full re-research to adopt the new structure.
  const isLegacyCard = useMemo(() => {
    if (!parsedResearch) return false;
    const newKeys = ['mission_programmes', 'public_activity_content', 'mission_alignment', 'funding_relationships_capacity', 'reputational_risk_check'];
    return !newKeys.some((k) => parsedResearch[k] !== undefined);
  }, [parsedResearch]);

  const handleReResearch = async () => {
    if (reResearching) return;
    if (!window.confirm('Run a full re-research? This regenerates the entire intelligence card from scratch under the latest structure and replaces the current profile. It can take a few minutes.')) return;
    setReResearching(true);
    try {
      const startRes = await researchApi.run(target.name);
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job ID returned');
      await pollJob({
        fetchStatus: () => researchApi.pollStatus(jobId).then(r => r.data),
        isComplete: (d) => d.status === 'complete',
        isError: (d) => d.status === 'error' || d.status === 'expired',
        getError: (d) => d.error || 'Re-research failed',
        onComplete: async () => {
          const updated = await targetsApi.getOne(id);
          setTarget(updated.data);
          toast.success('Full re-research complete — card rebuilt under the latest structure.');
        },
        onError: (msg) => toast.error(msg),
        intervalMs: 3000,
        maxPolls: 200,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReResearching(false);
    }
  };

  const ALLOWED_EXTS = ['.pdf', '.docx', '.txt', '.jpg', '.jpeg', '.png'];
  const MAX_FILE = 10 * 1024 * 1024;

  const handleCtxFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      setCtxFileError('Unsupported format. Supported: PDF, DOCX, TXT, JPG, PNG');
      return;
    }
    if (file.size > MAX_FILE) {
      setCtxFileError('File too large — try uploading individual sections or paste a summary');
      return;
    }
    setCtxFile(file);
    setCtxFileError('');
    setCtxFileExtracting(true);
    setCtxFileText('');
    try {
      const res = await targetsApi.extractFile(id, file);
      setCtxFileText(res.data.text);
    } catch (err) {
      setCtxFileError(err.response?.data?.detail || 'Could not read this file — try copying and pasting the content directly');
      setCtxFile(null);
    } finally {
      setCtxFileExtracting(false);
    }
  };

  const removeCtxFile = () => {
    setCtxFile(null);
    setCtxFileText('');
    setCtxFileError('');
    if (ctxFileInputRef.current) ctxFileInputRef.current.value = '';
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/(1024*1024)).toFixed(1)} MB`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [targetRes, projectsRes] = await Promise.all([
          targetsApi.getOne(id),
          projectsApi.getAll(),
        ]);
        setTarget(targetRes.data);
        setProjects(projectsRes.data);
        // Fetch import log
        try {
          const logRes = await targetsApi.getImportLog(id);
          setImportLog(logRes.data?.imports || []);
        } catch (e) {
          console.error('Failed to load import log:', e);
          toast.error(`Couldn't load import history: ${formatApiError(e)}`);
        }
        // Owner options + pipeline membership
        try {
          const [ownersRes, pipeRes] = await Promise.all([
            pipelineApi.ownerOptions(),
            pipelineApi.byTarget(id),
          ]);
          if (ownersRes.data?.owners) setOwnerOptions(ownersRes.data.owners);
          setInPipeline(!!pipeRes.data?.in_pipeline);
          setPipelineRecordId(pipeRes.data?.pipeline_record_id || null);
        } catch (e) {
          console.error('Failed to load owner options / pipeline membership:', e);
          toast.error(`Couldn't load owner & pipeline options: ${formatApiError(e)}`);
        }
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleDeltaScan = async () => {
    if (deltaScanRunning) return;
    setDeltaScanRunning(true);
    setDeltaScanProgress('Starting delta scan...');
    setDeltaScanResult(null);
    try {
      const res = await targetsApi.startDeltaScan(id);
      const jobId = res.data.job_id;
      await pollJob({
        fetchStatus: () => targetsApi.getDeltaScanStatus(id, jobId).then(r => r.data),
        isComplete: (d) => d.status === 'complete',
        isError: (d) => d.status === 'error' || d.status === 'expired',
        getError: (d) => d.error || 'Refresh failed',
        onProgress: (d) => setDeltaScanProgress(d.progress || `Searching... ${d.searches_done}/${d.total_searches}`),
        onComplete: async (d) => {
          setDeltaScanResult(d.result);
          setDeltaScanRunning(false);
          const updated = await targetsApi.getOne(id);
          setTarget(updated.data);
          try {
            const logRes = await targetsApi.getImportLog(id);
            setImportLog(logRes.data?.imports || []);
          } catch (e) {
            console.error('Failed to refresh import log after delta scan:', e);
            toast.error(`Couldn't refresh import history: ${formatApiError(e)}`);
          }
        },
        onError: (msg) => { setDeltaScanProgress(`Error: ${msg}`); setDeltaScanRunning(false); },
        intervalMs: 3000,
        maxPolls: 120,
      });
    } catch (err) {
      setDeltaScanProgress(`Failed: ${formatApiError(err)}`);
      setDeltaScanRunning(false);
    }
  };


  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  const handleAddToPipeline = async () => {
    if (!target?.id) return;
    if (inPipeline) { toast.info('This target is already in the pipeline.'); return; }
    setAddingToPipeline(true);
    try {
      const res = await pipelineApi.create({
        target_id: target.id,
        bd_work_types: target.bd_work_types || ['Commission'],
        project_ids: target.relevant_projects || [],
        owner: target.owner || 'Unassigned',
        stage: 'researched',
        temperature: 'cold',
      });
      setPipelineSuccess(true);
      setInPipeline(true);
      setPipelineRecordId(res.data?.id || null);
      toast.success('Added to pipeline');
    } catch (err) {
      if (err?.response?.status === 409) {
        setInPipeline(true);
        toast.info('This target is already in the pipeline.');
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setAddingToPipeline(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleDeleteTarget = async () => {
    setDeleting(true);
    try {
      await targetsApi.delete(id);
      navigate('/targets');
    } catch (err) {
      handleApiError(err, 'Failed to delete target');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleEditContact = (index) => {
    const contact = target.key_contacts[index];
    setContactDraft({
      name: contact.name || '',
      title: contact.title || contact.role || '',
      email: contact.email || '',
      linkedin: contact.linkedin || contact.linkedin_url || '',
      notes: contact.notes || '',
    });
    setEditingContact(index);
  };

  const handleSaveContact = async () => {
    if (editingContact === null) return;
    setSavingContact(true);
    try {
      await targetsApi.updateContact(id, editingContact, {
        name: contactDraft.name || null,
        title: contactDraft.title || null,
        email: contactDraft.email || null,
        linkedin: contactDraft.linkedin || null,
        notes: contactDraft.notes || null,
      });
      // Refresh target data
      const res = await targetsApi.getOne(id);
      setTarget(res.data);
      setEditingContact(null);
      setContactDraft({});
    } catch (err) {
      handleApiError(err, 'Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveInfoField = async (field, value) => {
    setSavingInfo(true);
    try {
      await targetsApi.updateContactInfo(id, { [field]: value });
      const res = await targetsApi.getOne(id);
      setTarget(res.data);
      setEditingInfoField(null);
      setInfoDraft('');
    } catch (err) {
      handleApiError(err, 'Failed to save changes');
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSaveSection = async (sectionKey, value) => {
    setSavingSection(true);
    try {
      await targetsApi.updateSection(id, sectionKey, value);
      const res = await targetsApi.getOne(id);
      setTarget(res.data);
      setEditingSection(null);
      setSectionDraft('');
    } catch (err) {
      handleApiError(err, 'Failed to save section');
    } finally {
      setSavingSection(false);
    }
  };

  const handleOwnerChange = async (newOwner) => {
    setUpdatingOwner(true);
    try {
      await targetsApi.updateOwner(id, newOwner);
      const res = await targetsApi.getOne(id);
      setTarget(res.data);
    } catch (err) {
      handleApiError(err, 'Failed to update owner');
    } finally {
      setUpdatingOwner(false);
    }
  };

  const isSectionEdited = (sectionKey) => {
    return target?.section_edits?.some(e => e.section === sectionKey);
  };

  const handleSaveOverride = async (field, value) => {
    setSavingOverride(true);
    try {
      await targetsApi.setOverride(id, field, value);
      const res = await targetsApi.getOne(id);
      setTarget(res.data);
      syncTarget(res.data);
      setOverrideEditing(null);
      setOverrideDraft(null);
      toast.success('Override saved');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingOverride(false);
    }
  };

  const handleClearOverride = async (field) => {
    setSavingOverride(true);
    try {
      await targetsApi.setOverride(id, field, null);
      const res = await targetsApi.getOne(id);
      setTarget(res.data);
      syncTarget(res.data);
      setOverrideEditing(null);
      setOverrideDraft(null);
      toast.success('Reverted to AI value');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingOverride(false);
    }
  };

  const toggleShowOriginal = (key) => setShowOriginal((p) => ({ ...p, [key]: !p[key] }));

  // ---- Reusable Components ----
  const RelevanceScore = ({ score }) => (
    <div className="flex items-center gap-2 justify-end">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={`w-2 h-8 rounded-full transition-all ${i <= score ? 'bg-[#00D4C8]' : 'bg-[#1A2F4C]'}`} />
      ))}
      <span className="text-sm text-[#00D4C8] ml-2">{score}/5</span>
    </div>
  );

  // Subtle indicator that a manual override is active. Click reveals the AI original.
  const EditedTag = ({ originalKey }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggleShowOriginal(originalKey); }}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-amber-500/10 text-amber-400 rounded border border-amber-500/20 hover:bg-amber-500/20 transition-colors align-middle"
      title="Manually edited — click to see the AI's original"
      data-testid={`edited-tag-${originalKey}`}
    >
      <Pencil className="w-2.5 h-2.5" /> Edited
    </button>
  );

  // Shared reputational / red-flag override editor. `aiRep` is the AI original
  // ({verdict, details}); the override lives in target.reputational_override.
  const renderReputationalOverride = (aiRep, prefix) => {
    const ai = aiRep || {};
    const ov = target?.reputational_override;
    const hasOverride = ov != null;
    const eff = hasOverride ? ov : ai;
    const isEditing = overrideEditing === 'reputational';
    return (
      <div data-testid={`${prefix}-rep-block`}>
        {isEditing ? (
          <div className="space-y-3" data-testid={`${prefix}-rep-editor`}>
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Verdict</label>
              <select
                value={overrideDraft?.verdict ?? ''}
                onChange={(e) => setOverrideDraft({ ...(overrideDraft || {}), verdict: e.target.value })}
                className="bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm rounded px-2 py-1 focus:border-[#00D4C8]"
                data-testid={`${prefix}-rep-verdict-select`}
              >
                <option value="">—</option>
                <option value="CLEAR">CLEAR</option>
                <option value="FLAG">FLAG</option>
                <option value="AVOID">AVOID</option>
              </select>
            </div>
            <textarea
              value={overrideDraft?.details ?? ''}
              onChange={(e) => setOverrideDraft({ ...(overrideDraft || {}), details: e.target.value })}
              rows={4}
              placeholder="Reasoning (leave empty to remove the flag content entirely)"
              className="w-full p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors resize-none"
              data-testid={`${prefix}-rep-details-input`}
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleSaveOverride('reputational', { verdict: (overrideDraft?.verdict || '').trim(), details: (overrideDraft?.details || '').trim() })}
                disabled={savingOverride}
                className="btn-primary text-xs flex items-center gap-1.5"
                data-testid={`${prefix}-rep-save-btn`}
              >
                {savingOverride ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save override
              </button>
              <button onClick={() => { setOverrideEditing(null); setOverrideDraft(null); }} className="btn-secondary text-xs" data-testid={`${prefix}-rep-cancel-btn`}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-2">
              {hasOverride && <EditedTag originalKey="reputational" />}
              <button
                onClick={() => { setOverrideEditing('reputational'); setOverrideDraft({ verdict: eff?.verdict || '', details: eff?.details || '' }); }}
                className="inline-flex items-center gap-1 text-xs text-[#A3B5C7] hover:text-[#00D4C8] transition-colors"
                title="Override verdict / reasoning"
                data-testid={`${prefix}-rep-edit-btn`}
              >
                <Pencil className="w-3 h-3" /> Override
              </button>
            </div>
            {eff?.details ? (
              <p className="text-[#D4E2F0] leading-relaxed whitespace-pre-wrap" data-testid={`${prefix}-rep-details`}>{eff.details}</p>
            ) : <EmptyPlaceholder />}
            {hasOverride && showOriginal.reputational && (
              <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-amber-500/20 text-sm" data-testid={`${prefix}-rep-original`}>
                <p className="text-xs uppercase tracking-wider text-amber-400 mb-1">AI original</p>
                <p className="text-[#A3B5C7]"><span className="font-medium">Verdict:</span> {ai?.verdict || '—'}</p>
                <p className="text-[#A3B5C7] whitespace-pre-wrap"><span className="font-medium">Reasoning:</span> {ai?.details || '—'}</p>
                <button onClick={() => handleClearOverride('reputational')} disabled={savingOverride} className="mt-2 text-[#00D4C8] hover:underline text-xs" data-testid={`${prefix}-rep-clear-btn`}>Revert to AI verdict</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const RatingBadge = ({ rating }) => {
    if (!rating) return null;
    const normalized = String(rating).toUpperCase();
    const colors = {
      HIGH: 'bg-green-500/20 text-green-400 border-green-500/30',
      STRONG: 'bg-green-500/20 text-green-400 border-green-500/30',
      'STRONG FIT': 'bg-green-500/20 text-green-400 border-green-500/30',
      MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      MODERATE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'POSSIBLE FIT': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      LOW: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      WEAK: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'NO FIT': 'bg-red-500/20 text-red-400 border-red-500/30',
      NONE: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      CLEAR: 'bg-green-500/20 text-green-400 border-green-500/30',
      FLAG: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      AVOID: 'bg-red-500/20 text-red-400 border-red-500/30',
      YES: 'bg-green-500/20 text-green-400 border-green-500/30',
      NO: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      PARTIAL: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      ACTIVE: 'bg-green-500/20 text-green-400 border-green-500/30',
      POSSIBLE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      UNLIKELY: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      RELEASED: 'bg-green-500/20 text-green-400 border-green-500/30',
      'IN PRODUCTION': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'IN DEVELOPMENT': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return (
      <span className={`px-2 py-1 text-xs uppercase tracking-wide rounded border ${colors[normalized] || colors.NONE}`}>
        {rating}
      </span>
    );
  };

  const ConfidenceBadge = ({ level, notes }) => {
    if (!level) return null;
    const normalized = String(level).toUpperCase();
    const config = {
      HIGH: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Shield },
      MEDIUM: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: BarChart3 },
      LOW: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertTriangle },
    };
    const c = config[normalized] || config.MEDIUM;
    const Icon = c.icon;
    return (
      <div className="flex items-center gap-3">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${c.color}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm font-medium uppercase tracking-wide">Data Confidence: {level}</span>
        </div>
        {notes && <span className="text-xs text-[#A3B5C7]">{notes}</span>}
      </div>
    );
  };

  // DEFECT A: sections whose content is SYNTHESISED (derived). When a confirmed
  // import changes a source-fed field, the backend sets target.derived_stale and
  // these sections show a STALE badge until research is re-run.
  const DERIVED_SECTION_IDS = new Set([
    'sustainability', 'mission_alignment', 'fit_assessment', 'funding_capacity',
    'greenwash', 'red_flag', 'org_engagement', 'engagement', 'giving_capacity',
    'bd_angles', 'pitch_hooks', 'pitch', 'relevance',
  ]);

  const CollapsibleSection = ({ id: sectionId, title, icon: Icon, children, badge, editableKey, editableValue }) => (
    <div className="card-surface">
      <div onClick={() => toggleSection(sectionId)} className="w-full p-6 flex items-center justify-between text-left cursor-pointer" role="button" tabIndex={0} data-testid={`section-toggle-${sectionId}`}>
        <div className="flex items-center gap-3 flex-wrap">
          {Icon && <Icon className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />}
          <h3 className="font-heading text-xl text-white">{title}</h3>
          {badge}
          {isSectionEdited(editableKey) && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider bg-amber-500/10 text-amber-400 rounded border border-amber-500/20">Edited</span>
          )}
          {target?.derived_stale && DERIVED_SECTION_IDS.has(sectionId) && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-400 rounded border border-amber-500/30 flex items-center gap-1" data-testid={`section-stale-${sectionId}`} title="Source data changed since last research">
              <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2} /> Stale — re-run research to refresh
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editableKey && expandedSections[sectionId] && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingSection(editableKey);
                setSectionDraft(typeof editableValue === 'string' ? editableValue : JSON.stringify(editableValue || '', null, 2));
              }}
              className="p-1.5 rounded-lg text-[#A3B5C7] hover:text-[#00D4C8] hover:bg-[#00D4C8]/10 transition-all"
              title={`Edit ${title}`}
              data-testid={`section-edit-${editableKey}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {expandedSections[sectionId] ? <ChevronUp className="w-5 h-5 text-[#A3B5C7]" /> : <ChevronDown className="w-5 h-5 text-[#A3B5C7]" />}
        </div>
      </div>
      {expandedSections[sectionId] && (
        <div className="px-6 pb-6">
          {editingSection === editableKey && editableKey ? (
            <div className="space-y-3" data-testid={`section-editing-${editableKey}`}>
              <textarea
                key={`edit-${editableKey}`}
                ref={sectionEditRef}
                defaultValue={sectionDraft}
                rows={10}
                className="w-full p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors resize-y font-sans"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveSection(editableKey, sectionEditRef.current ? sectionEditRef.current.value : sectionDraft)}
                  disabled={savingSection}
                  className="btn-primary text-xs flex items-center gap-1.5"
                  data-testid={`section-save-${editableKey}`}
                >
                  {savingSection ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save
                </button>
                <button
                  onClick={() => { setEditingSection(null); setSectionDraft(''); }}
                  className="btn-secondary text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="group/section relative">
              {editableKey && (
                <button
                  onClick={() => {
                    setEditingSection(editableKey);
                    setSectionDraft(typeof editableValue === 'string' ? editableValue : JSON.stringify(editableValue || '', null, 2));
                  }}
                  className="absolute top-0 right-0 p-1.5 rounded-lg text-[#A3B5C7]/40 hover:text-[#00D4C8] hover:bg-[#00D4C8]/10 transition-all opacity-0 group-hover/section:opacity-100"
                  title={`Edit ${title}`}
                  data-testid={`section-edit-btn-${editableKey}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              {children}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderPersonLink = (person) => {
    if (!person) return null;
    if (typeof person === 'string') return <span className="text-white">{person}</span>;
    return (
      <div className="flex items-center gap-2">
        <span className="text-white">{person.name}</span>
        {person.company && <span className="text-[#A3B5C7] text-sm">({person.company})</span>}
        {person.linkedin && (
          <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#00D4C8] hover:text-[#33DDD3]">
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </a>
        )}
      </div>
    );
  };

  const isIndividual = target?.type === 'individual' || parsedResearch?.type === 'individual';
  const isFilm = parsedResearch?.type === 'film_intelligence';

  // Auto-expand reputational risk if FLAG or AVOID
  useEffect(() => {
    const rep = firstNonEmpty(parsedResearch?.reputational_risk_check, parsedResearch?.greenwash_check);
    if (rep) {
      const v = rep.verdict;
      if (v === 'FLAG' || v === 'AVOID') {
        setExpandedSections(prev => ({ ...prev, greenwash: true }));
      }
    }
  }, [parsedResearch]);

  if (loading) {
    return (
      <div className="">
        <div className="max-w-4xl mx-auto">
          <div className="card-surface h-64 shimmer mb-6" />
          <div className="card-surface h-32 shimmer mb-4" />
          <div className="card-surface h-32 shimmer" />
        </div>
      </div>
    );
  }

  if (error || !target) {
    return (
      <div className="">
        <div className="max-w-4xl mx-auto text-center py-16">
          <p className="text-red-400 mb-4">{error || 'Target not found'}</p>
          <Link to="/targets" className="btn-primary">Back to Targets</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-4xl mx-auto">
        <Link to="/targets" className="inline-flex items-center gap-2 text-[#A3B5C7] hover:text-white mb-6 transition-colors" data-testid="back-to-targets">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Targets
        </Link>

        {/* Header card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface card-glow p-8 mb-6" data-testid="target-header">
          {/* Delta scan update note */}
          {target.delta_scan_note && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-[#00D4C8]/5 border border-[#00D4C8]/20 text-sm text-[#00D4C8]" data-testid="delta-scan-note">
              {target.delta_scan_note}
            </div>
          )}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
                  {isFilm ? <Clapperboard className="w-6 h-6 text-amber-400" strokeWidth={1.5} /> :
                   isIndividual ? <User className="w-6 h-6 text-[#00D4C8]" strokeWidth={1.5} /> :
                   <Building className="w-6 h-6 text-[#00D4C8]" strokeWidth={1.5} />}
                </div>
                <div>
                  <h1 className="font-heading text-3xl text-white" data-testid="target-name">
                    {isFilm ? (parsedResearch?.title || target.name) : (target.name || parsedResearch?.full_name)}
                  </h1>
                  <p className="text-[#A3B5C7]">
                    {isFilm ? (
                      <span className="flex items-center gap-2">
                        {parsedResearch?.genre_subject}
                        {parsedResearch?.year && <span className="text-[#00D4C8]">({parsedResearch.year})</span>}
                        {parsedResearch?.status && <RatingBadge rating={parsedResearch.status} />}
                      </span>
                    ) : isIndividual ? parsedResearch?.current_role : target.type}
                  </p>
                  {isIndividual && parsedResearch?.organisation && <p className="text-sm text-[#A3B5C7]">{parsedResearch.organisation}</p>}
                  {isFilm && parsedResearch?.runtime && <p className="text-sm text-[#A3B5C7]">{parsedResearch.runtime}</p>}
                </div>
              </div>
            </div>
            <div className="text-right space-y-2">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 rounded-lg text-[#A3B5C7] hover:text-red-400 hover:bg-red-400/10 transition-colors"
                title="Delete target"
                data-testid="delete-target-btn"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
              {(target.relevance_score || target.relevance_score_override != null) && (() => {
                const hasOverride = target.relevance_score_override != null;
                const displayScore = hasOverride ? target.relevance_score_override : target.relevance_score;
                return (
                <div data-testid="relevance-block">
                  <div className="flex items-center justify-end gap-2 mb-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-[#A3B5C7]">Relevance</p>
                    {hasOverride && <EditedTag originalKey="relevance" />}
                    <button
                      onClick={() => { setOverrideEditing('relevance'); setOverrideDraft(String(displayScore || 3)); }}
                      className="p-1 rounded text-[#A3B5C7]/60 hover:text-[#00D4C8] hover:bg-[#00D4C8]/10 transition-all"
                      title="Edit relevance score"
                      data-testid="relevance-edit-btn"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                  {overrideEditing === 'relevance' ? (
                    <div className="flex items-center gap-2 justify-end" data-testid="relevance-editor">
                      <select
                        value={overrideDraft ?? ''}
                        onChange={(e) => setOverrideDraft(e.target.value)}
                        className="bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm rounded px-2 py-1 focus:border-[#00D4C8]"
                        data-testid="relevance-select"
                      >
                        {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}/5</option>)}
                      </select>
                      <button onClick={() => handleSaveOverride('relevance_score', parseInt(overrideDraft, 10))} disabled={savingOverride} className="btn-primary text-xs flex items-center gap-1" data-testid="relevance-save-btn">
                        {savingOverride ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </button>
                      <button onClick={() => { setOverrideEditing(null); setOverrideDraft(null); }} className="btn-secondary text-xs" data-testid="relevance-cancel-btn"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <>
                      <RelevanceScore score={displayScore} />
                      {hasOverride && showOriginal.relevance && (
                        <div className="mt-2 flex items-center justify-end gap-2 text-xs text-[#A3B5C7]" data-testid="relevance-original">
                          <span>AI original: {target.relevance_score}/5</span>
                          <button onClick={() => handleClearOverride('relevance_score')} disabled={savingOverride} className="text-[#00D4C8] hover:underline" data-testid="relevance-clear-btn">Revert</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                );
              })()}
              {isFilm && parsedResearch?.data_confidence && (
                <ConfidenceBadge level={parsedResearch.data_confidence} />
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            {(() => {
              const aiLocation = target.hq_location || parsedResearch?.location || '';
              const hasOverride = !!target.location_override;
              const displayLocation = hasOverride ? target.location_override : aiLocation;
              if (!displayLocation && overrideEditing !== 'location') return null;
              return (
                <div className="flex items-center gap-2 text-[#A3B5C7]" data-testid="location-block">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  {overrideEditing === 'location' ? (
                    <span className="flex items-center gap-2" data-testid="location-editor">
                      <input
                        value={overrideDraft || ''}
                        onChange={(e) => setOverrideDraft(e.target.value)}
                        placeholder="Location"
                        className="bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm rounded px-2 py-1 focus:border-[#00D4C8]"
                        autoFocus
                        data-testid="location-input"
                      />
                      <button onClick={() => handleSaveOverride('location', (overrideDraft || '').trim())} disabled={savingOverride || !(overrideDraft || '').trim()} className="text-[#00D4C8] disabled:opacity-40" data-testid="location-save-btn">
                        {savingOverride ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => { setOverrideEditing(null); setOverrideDraft(null); }} className="text-[#A3B5C7] hover:text-white" data-testid="location-cancel-btn"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  ) : (
                    <>
                      <span>{displayLocation}</span>
                      {hasOverride && <EditedTag originalKey="location" />}
                      <button onClick={() => { setOverrideEditing('location'); setOverrideDraft(displayLocation); }} className="p-0.5 rounded text-[#A3B5C7]/50 hover:text-[#00D4C8] transition-colors" title="Edit location" data-testid="location-edit-btn">
                        <Pencil className="w-3 h-3" />
                      </button>
                      {hasOverride && showOriginal.location && (
                        <span className="text-xs text-[#A3B5C7]/70 flex items-center gap-2" data-testid="location-original">
                          · AI: {aiLocation || '—'}
                          <button onClick={() => handleClearOverride('location')} disabled={savingOverride} className="text-[#00D4C8] hover:underline" data-testid="location-clear-btn">Revert</button>
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
            {(target.website || parsedResearch?.linkedin_url) && (
              <a href={target.website || parsedResearch?.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#00D4C8] hover:text-[#33DDD3] transition-colors">
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                {target.website ? 'Website' : 'LinkedIn'}
              </a>
            )}
            {target.research_date && (
              <div className="flex items-center gap-2 text-[#A3B5C7]">
                <Calendar className="w-4 h-4" strokeWidth={1.5} />
                Researched {formatDate(target.research_date)}
              </div>
            )}
          </div>

          {/* Ownership row */}
          <div className="flex items-center gap-4 mt-4 text-sm" data-testid="target-ownership-row">
            {(target.created_by_name || target.owner) && (
              <div className="flex items-center gap-1.5 text-[#A3B5C7]">
                <User className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>Added by {target.created_by_name || target.owner}</span>
                {target.created_at && (
                  <span className="text-[#A3B5C7]/50">· {formatDate(target.created_at)}</span>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase tracking-wider text-[#A3B5C7]/60">Owner:</span>
              <select
                value={target.owner || ''}
                onChange={(e) => handleOwnerChange(e.target.value)}
                disabled={updatingOwner}
                className="bg-transparent text-[#00D4C8] text-sm font-medium cursor-pointer border-none focus:ring-0 focus:outline-none appearance-none pr-4"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2300D4C8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right center' }}
                data-testid="target-owner-select"
              >
                {ownerOptions.map((o) => (
                  <option key={o} value={o === 'Unassigned' ? '' : o}>{o}</option>
                ))}
              </select>
            </div>
          </div>

          {Array.isArray(target.bd_work_types) && target.bd_work_types.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6" data-testid="bd-work-types">
              {target.bd_work_types.map((type) => (
                <span key={type} className="px-3 py-1 text-xs uppercase tracking-[0.1em] bg-[#00D4C8]/10 text-[#00D4C8] rounded-full border border-[#00D4C8]/20">{type}</span>
              ))}
            </div>
          )}

          {isIndividual && Array.isArray(parsedResearch?.relationship_types) && parsedResearch.relationship_types.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {parsedResearch.relationship_types.map((type) => (
                <span key={type} className="px-3 py-1 text-xs uppercase tracking-[0.1em] bg-white/10 text-white rounded-full border border-white/20">{type}</span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-[#1A2F4C]">
            {inPipeline ? (
              <button
                onClick={() => navigate('/pipeline')}
                className="btn-primary flex items-center gap-2 bg-[#0E1C33] border border-[#00D4C8]/40 text-[#00D4C8]"
                data-testid="in-pipeline-btn"
              >
                <Check className="w-4 h-4" /> In Pipeline
              </button>
            ) : (
              <button onClick={handleAddToPipeline} disabled={addingToPipeline} className="btn-primary flex items-center gap-2" data-testid="add-to-pipeline-btn">
                {addingToPipeline ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" strokeWidth={2} /> Add to Pipeline</>}
              </button>
            )}
            <button onClick={handleDeltaScan} disabled={deltaScanRunning} className="btn-secondary flex items-center gap-2 border-[#00D4C8]/30 hover:border-[#00D4C8]/60" data-testid="refresh-intelligence-btn">
              {deltaScanRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning...</> : <><Zap className="w-4 h-4 text-[#00D4C8]" /> Refresh Intelligence</>}
            </button>
            {isLegacyCard && (
              <button onClick={handleReResearch} disabled={reResearching} className="btn-secondary flex items-center gap-2 border-amber-400/40 hover:border-amber-400/70" data-testid="re-research-btn">
                {reResearching ? <><Loader2 className="w-4 h-4 animate-spin" /> Re-researching...</> : <><RefreshCw className="w-4 h-4 text-amber-400" /> Run Full Re-research</>}
              </button>
            )}
            <button
              onClick={async () => {
                try {
                  const res = await exportApi.targetPdf(id);
                  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url; a.download = `${target.name}_report.pdf`; a.click();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  handleApiError(err, 'Failed to export PDF');
                }
              }}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-target-pdf-btn"
            >
              Export PDF
            </button>
            <button
              onClick={() => setShowBriefing(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="generate-meeting-brief-btn"
            >
              <FileText className="w-4 h-4" /> Meeting Brief
            </button>
          </div>

          <p className="text-xs text-[#A3B5C7]/70 mt-3" data-testid="refresh-intelligence-note">
            <span className="text-[#00D4C8]">Refresh Intelligence</span> checks for new developments since the last full research and adds them as an update note — it does not rebuild the card. To regenerate the full profile under the latest structure, run a full re-research.
          </p>

          {/* Delta Scan Progress / Result */}
          {(deltaScanRunning || deltaScanResult) && (
            <div className={`mt-4 p-4 rounded-lg border ${deltaScanRunning ? 'bg-[#00D4C8]/5 border-[#00D4C8]/20' : deltaScanResult?.delta_report?.has_new_intelligence ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-[#1A2F4C]/30 border-[#1A2F4C]'}`} data-testid="delta-scan-status">
              {deltaScanRunning && (
                <div className="flex items-center gap-2 text-sm text-[#00D4C8]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{deltaScanProgress}</span>
                </div>
              )}
              {!deltaScanRunning && deltaScanResult && (
                <div>
                  <p className="text-sm font-medium text-white">{deltaScanResult.update_note}</p>
                  {deltaScanResult.delta_report?.has_new_intelligence && (
                    <div className="mt-3 space-y-2">
                      {firstNonEmpty(deltaScanResult.delta_report.public_activity_content, deltaScanResult.delta_report.active_video_evidence)?.found && (
                        <div className="text-xs">
                          <span className="text-[#00D4C8] font-medium uppercase tracking-wider">Public Activity: </span>
                          <span className="text-[#D4E2F0]">{(firstNonEmpty(deltaScanResult.delta_report.public_activity_content, deltaScanResult.delta_report.active_video_evidence).details || []).join('; ')}</span>
                        </div>
                      )}
                      {(deltaScanResult.delta_report.press_coverage || []).length > 0 && (
                        <div className="text-xs">
                          <span className="text-[#00D4C8] font-medium uppercase tracking-wider">Press: </span>
                          <span className="text-[#D4E2F0]">{deltaScanResult.delta_report.press_coverage.map(p => p.title).join('; ')}</span>
                        </div>
                      )}
                      {(deltaScanResult.delta_report.programme_announcements || []).length > 0 && (
                        <div className="text-xs">
                          <span className="text-[#00D4C8] font-medium uppercase tracking-wider">Programmes: </span>
                          <span className="text-[#D4E2F0]">{deltaScanResult.delta_report.programme_announcements.map(p => p.title || p.details).join('; ')}</span>
                        </div>
                      )}
                      {(deltaScanResult.delta_report.suggested_actions || []).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[#1A2F4C]">
                          <span className="text-[10px] text-[#00D4C8] font-bold uppercase tracking-wider">Suggested Actions:</span>
                          <ul className="mt-1 space-y-1">
                            {deltaScanResult.delta_report.suggested_actions.map((a, i) => (
                              <li key={i} className="text-xs text-[#D4E2F0] flex items-start gap-2">
                                <span className="text-[#00D4C8] mt-0.5">•</span> {a}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <button onClick={() => setDeltaScanResult(null)} className="text-[10px] text-[#A3B5C7] hover:text-white mt-2 underline">Dismiss</button>
                  {deltaScanResult.delta_report?.has_new_intelligence && (
                    <div className="mt-3 pt-3 border-t border-[#1A2F4C] flex items-center justify-between gap-3">
                      <span className="text-[11px] text-[#A3B5C7]">Significant new intelligence found. Run a full re-research to rebuild the card under the latest structure.</span>
                      <button onClick={handleReResearch} disabled={reResearching} className="btn-secondary text-xs px-3 py-1 flex items-center gap-1.5 border-amber-400/40 hover:border-amber-400/70 shrink-0" data-testid="delta-re-research-btn">
                        {reResearching ? <><Loader2 className="w-3 h-3 animate-spin" /> Working...</> : <><RefreshCw className="w-3 h-3 text-amber-400" /> Full Re-research</>}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* ===== CONTACT INFORMATION PANEL ===== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-surface p-6 mb-6" data-testid="contact-info-panel">
          <h3 className="text-xs uppercase tracking-[0.15em] text-[#A3B5C7] font-medium mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {[
              { key: 'email', icon: Mail, label: 'Email', placeholder: 'Add email', linkPrefix: 'mailto:' },
              { key: 'phone', icon: Phone, label: 'Phone', placeholder: 'Add phone', linkPrefix: 'tel:' },
              { key: 'website', icon: Globe, label: 'Website', placeholder: 'Add website', linkPrefix: '' },
              { key: 'linkedin_url', icon: ExternalLink, label: 'LinkedIn', placeholder: 'Add LinkedIn URL', linkPrefix: '' },
              { key: 'youtube_url', icon: Youtube, label: 'YouTube', placeholder: 'Add YouTube URL', linkPrefix: '' },
              { key: 'instagram_url', icon: Instagram, label: 'Instagram', placeholder: 'Add Instagram', linkPrefix: '' },
              { key: 'twitter_url', icon: Twitter, label: 'X / Twitter', placeholder: 'Add X handle', linkPrefix: '' },
              { key: 'other_url', icon: Globe, label: 'Other', placeholder: 'Add other URL', linkPrefix: '' },
            ].map(({ key, icon: FieldIcon, label, placeholder, linkPrefix }) => {
              const value = target.contact_info?.[key] || '';
              const isEditing = editingInfoField === key;

              return (
                <div key={key} className="flex items-center gap-2 py-1.5 group min-h-[36px]" data-testid={`info-field-${key}`}>
                  <FieldIcon className="w-4 h-4 text-[#A3B5C7]/60 shrink-0" strokeWidth={1.5} />
                  {isEditing ? (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <input
                        autoFocus
                        value={infoDraft}
                        onChange={(e) => setInfoDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveInfoField(key, infoDraft); if (e.key === 'Escape') { setEditingInfoField(null); setInfoDraft(''); } }}
                        placeholder={placeholder}
                        className="flex-1 min-w-0 px-2 py-1 rounded bg-[#0A1628] border border-[#1A2F4C] text-white text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors"
                        data-testid={`info-edit-${key}`}
                      />
                      <button
                        onClick={() => handleSaveInfoField(key, infoDraft)}
                        disabled={savingInfo}
                        className="p-1 rounded text-[#00D4C8] hover:bg-[#00D4C8]/10 transition-colors"
                        data-testid={`info-save-${key}`}
                      >
                        {savingInfo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => { setEditingInfoField(null); setInfoDraft(''); }}
                        className="p-1 rounded text-[#A3B5C7] hover:text-white transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : value ? (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      {linkPrefix !== undefined && (linkPrefix === 'mailto:' || linkPrefix === 'tel:' || value.startsWith('http')) ? (
                        <a
                          href={linkPrefix ? `${linkPrefix}${value}` : value}
                          target={linkPrefix === '' ? '_blank' : undefined}
                          rel={linkPrefix === '' ? 'noopener noreferrer' : undefined}
                          className="text-sm text-[#00D4C8] hover:text-[#33DDD3] transition-colors truncate"
                          data-testid={`info-link-${key}`}
                        >
                          {value}
                        </a>
                      ) : (
                        <span className="text-sm text-[#D4E2F0] truncate">{value}</span>
                      )}
                      <button
                        onClick={() => { setEditingInfoField(key); setInfoDraft(value); }}
                        className="p-1 rounded text-[#A3B5C7]/0 group-hover:text-[#A3B5C7] hover:text-[#00D4C8] transition-all"
                        title={`Edit ${label}`}
                        data-testid={`info-edit-btn-${key}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingInfoField(key); setInfoDraft(''); }}
                      className="text-sm text-[#A3B5C7]/40 hover:text-[#A3B5C7] transition-colors cursor-pointer"
                      data-testid={`info-add-btn-${key}`}
                    >
                      {placeholder}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Report sections */}
        <div className="space-y-4" data-testid="report-sections">

          {/* ===== FILM INTELLIGENCE SECTIONS ===== */}
          {isFilm && parsedResearch && (
            <>
              {/* Data Confidence */}
              {parsedResearch.data_confidence && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                  <CollapsibleSection id="confidence" title="Data Confidence" icon={Shield} badge={<ConfidenceBadge level={parsedResearch.data_confidence} />}>
                    {parsedResearch.data_confidence_notes && <p className="text-[#D4E2F0] leading-relaxed">{parsedResearch.data_confidence_notes}</p>}
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Key People */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <CollapsibleSection id="key_people" title="Key People" icon={Users}>
                  <div className="space-y-4">
                    {parsedResearch.director && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-1 block">Director</label>
                        {renderPersonLink(parsedResearch.director)}
                      </div>
                    )}
                    {Array.isArray(parsedResearch.producers) && parsedResearch.producers.length > 0 && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Producers</label>
                        <div className="space-y-2">{parsedResearch.producers.map((p, i) => <div key={i}>{renderPersonLink(p)}</div>)}</div>
                      </div>
                    )}
                    {Array.isArray(parsedResearch.executive_producers) && parsedResearch.executive_producers.length > 0 && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Executive Producers</label>
                        <div className="space-y-2">{parsedResearch.executive_producers.map((p, i) => <div key={i}>{renderPersonLink(p)}</div>)}</div>
                      </div>
                    )}
                    {parsedResearch.sales_agent && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-1 block">Sales Agent</label>
                        {typeof parsedResearch.sales_agent === 'string' ? (
                          <span className="text-white">{parsedResearch.sales_agent}</span>
                        ) : (
                          <div>
                            <span className="text-white">{parsedResearch.sales_agent.company}</span>
                            {parsedResearch.sales_agent.contact && <span className="text-[#A3B5C7] text-sm ml-2">({parsedResearch.sales_agent.contact})</span>}
                            {parsedResearch.sales_agent.territories && <span className="text-[#A3B5C7] text-sm ml-2">— {parsedResearch.sales_agent.territories}</span>}
                          </div>
                        )}
                      </div>
                    )}
                    {parsedResearch.commissioning_platform && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-1 block">Commissioning Platform</label>
                        <span className="text-white">{parsedResearch.commissioning_platform}</span>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              </motion.div>

              {/* Financing Structure */}
              {(parsedResearch.financing_sources || parsedResearch.financing_summary || parsedResearch.budget_range) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                  <CollapsibleSection id="financing" title="Financing Structure" icon={DollarSign}>
                    <div className="space-y-4">
                      {parsedResearch.budget_range && (
                        <div className="inline-block px-4 py-2 rounded-lg bg-[#00D4C8]/10 border border-[#00D4C8]/20">
                          <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">Budget Range: </span>
                          <span className="text-[#00D4C8] font-medium">{parsedResearch.budget_range}</span>
                        </div>
                      )}
                      {Array.isArray(parsedResearch.financing_sources) && parsedResearch.financing_sources.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-[#1A2F4C]">
                                <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Source Type</th>
                                <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Entity</th>
                                <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parsedResearch.financing_sources.map((s, i) => (
                                <tr key={i} className="border-b border-[#1A2F4C]/50">
                                  <td className="py-3 px-3 text-[#00D4C8] text-xs uppercase">{typeof s === 'string' ? s : s.type}</td>
                                  <td className="py-3 px-3 text-white">{typeof s === 'string' ? '' : s.entity}</td>
                                  <td className="py-3 px-3 text-[#D4E2F0]">{typeof s === 'string' ? '' : s.details}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {parsedResearch.financing_summary && <p className="text-[#D4E2F0] leading-relaxed">{parsedResearch.financing_summary}</p>}
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Distribution & Sales */}
              {(parsedResearch.distribution_platforms || parsedResearch.territories || parsedResearch.viewing_figures || parsedResearch.festival_run) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <CollapsibleSection id="distribution" title="Distribution & Sales" icon={TrendingUp}>
                    <div className="space-y-4">
                      {Array.isArray(parsedResearch.distribution_platforms) && parsedResearch.distribution_platforms.length > 0 && (
                        <div>
                          <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Platforms</label>
                          <div className="flex flex-wrap gap-2">
                            {parsedResearch.distribution_platforms.map((p, i) => <span key={i} className="px-3 py-1 bg-white/10 text-white rounded-lg text-sm">{p}</span>)}
                          </div>
                        </div>
                      )}
                      {Array.isArray(parsedResearch.territories) && parsedResearch.territories.length > 0 && (
                        <div>
                          <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Territories</label>
                          <div className="flex flex-wrap gap-2">
                            {parsedResearch.territories.map((t, i) => <span key={i} className="px-2 py-0.5 text-xs bg-[#1A2F4C] text-[#D4E2F0] rounded">{t}</span>)}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {parsedResearch.theatrical_release && (
                          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                            <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Theatrical</label>
                            <p className="text-white capitalize mt-1">{parsedResearch.theatrical_release}</p>
                          </div>
                        )}
                        {parsedResearch.viewing_figures && (
                          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                            <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Viewing Figures</label>
                            <p className="text-white mt-1">{parsedResearch.viewing_figures}</p>
                          </div>
                        )}
                        {parsedResearch.festival_run && (
                          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                            <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Festival Run</label>
                            <p className="text-white mt-1">{parsedResearch.festival_run}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Impact Campaign */}
              {parsedResearch.impact_campaign && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                  <CollapsibleSection id="impact" title="Impact Campaign" icon={Leaf}>
                    <div className="space-y-4">
                      {typeof parsedResearch.impact_campaign === 'string' ? (
                        <p className="text-[#D4E2F0] leading-relaxed">{parsedResearch.impact_campaign}</p>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <RatingBadge rating={parsedResearch.impact_campaign.has_campaign ? 'YES' : 'NO'} />
                            <span className="text-[#A3B5C7] text-sm">Impact Campaign</span>
                          </div>
                          {Array.isArray(parsedResearch.impact_campaign.ngo_partners) && parsedResearch.impact_campaign.ngo_partners.length > 0 && (
                            <div>
                              <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">NGO Partners</label>
                              <div className="flex flex-wrap gap-2">
                                {parsedResearch.impact_campaign.ngo_partners.map((p, i) => (
                                  <span key={i} className="px-3 py-1 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">{p}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {parsedResearch.impact_campaign.impact_producer && (
                            <div>
                              <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Impact Producer</label>
                              <p className="text-white mt-1">{parsedResearch.impact_campaign.impact_producer}</p>
                            </div>
                          )}
                          {parsedResearch.impact_campaign.screening_strategy && (
                            <div>
                              <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Screening Strategy</label>
                              <p className="text-[#D4E2F0] mt-1">{parsedResearch.impact_campaign.screening_strategy}</p>
                            </div>
                          )}
                          {parsedResearch.impact_campaign.measurable_outcomes && (
                            <div>
                              <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Measurable Outcomes</label>
                              <p className="text-[#D4E2F0] mt-1">{parsedResearch.impact_campaign.measurable_outcomes}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Awards & Recognition */}
              {Array.isArray(parsedResearch.awards) && parsedResearch.awards.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <CollapsibleSection id="awards" title="Awards & Recognition" icon={Award}>
                    <div className="space-y-3">
                      <ul className="space-y-2">
                        {parsedResearch.awards.map((award, i) => (
                          <li key={i} className="flex items-center gap-2 text-[#D4E2F0]">
                            <Star className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                            {award}
                          </li>
                        ))}
                      </ul>
                      {parsedResearch.critical_reception && (
                        <p className="text-[#A3B5C7] italic mt-3 border-l-2 border-[#1A2F4C] pl-4">{parsedResearch.critical_reception}</p>
                      )}
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Comparability */}
              {parsedResearch.comparable_use && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                  <CollapsibleSection id="comparables" title="Comparability" icon={BookOpen}>
                    <div className="space-y-4">
                      <p className="text-[#D4E2F0] italic border-l-2 border-[#00D4C8] pl-4">{parsedResearch.comparable_use}</p>
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}
            </>
          )}

          {/* ===== ORGANISATION SECTIONS ===== */}
          {!isFilm && !isIndividual && (
            <>
              {/* 1. Mission & Programmes */}
              {(() => {
                const mission = firstNonEmpty(parsedResearch?.mission_programmes, parsedResearch?.mandate_summary, target.mandate_summary);
                return (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <CollapsibleSection id="mandate" title="Mission & Programmes" icon={Target} editableKey="mission_programmes" editableValue={mission}>
                    {mission ? <p className="text-[#D4E2F0] leading-relaxed whitespace-pre-wrap">{mission}</p> : <EmptyPlaceholder />}
                  </CollapsibleSection>
                </motion.div>
                );
              })()}

              {/* Public Activity & Content */}
              {(() => {
                const vci = parsedResearch?.video_content_intelligence;
                const pac = firstNonEmpty(parsedResearch?.public_activity_content, parsedResearch?.active_video_content_evidence, parsedResearch?.active_video_evidence);
                const pacText = typeof pac === 'string' ? pac : (pac && typeof pac === 'object' ? pac.details : null);
                return (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <CollapsibleSection id="video_intelligence" title="Public Activity & Content" icon={PlayCircle} badge={vci?.production_quality && <RatingBadge rating={vci.production_quality} />} editableKey="public_activity_content" editableValue={pacText}>
                      {vci ? (
                        <div className="space-y-4">
                          {vci.youtube && (typeof vci.youtube === 'object') && (
                            <div className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                              <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-3 font-medium">YouTube</h4>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {vci.youtube.channel_url && <div><span className="text-[#A3B5C7]">Channel:</span> <a href={vci.youtube.channel_url} target="_blank" rel="noopener noreferrer" className="text-[#00D4C8] hover:underline ml-1">{vci.youtube.channel_url.replace('https://www.youtube.com/', '').replace('https://youtube.com/', '')}</a></div>}
                                {vci.youtube.subscriber_count && <div><span className="text-[#A3B5C7]">Subscribers:</span> <span className="text-white font-medium ml-1">{vci.youtube.subscriber_count}</span></div>}
                                {vci.youtube.posting_frequency && <div><span className="text-[#A3B5C7]">Frequency:</span> <span className="text-[#D4E2F0] ml-1">{vci.youtube.posting_frequency}</span></div>}
                                {vci.youtube.average_views && <div><span className="text-[#A3B5C7]">Avg Views:</span> <span className="text-[#D4E2F0] ml-1">{vci.youtube.average_views}</span></div>}
                              </div>
                              {vci.youtube.top_recent_content && <p className="text-[#D4E2F0] text-sm mt-2"><span className="text-[#A3B5C7]">Top Content:</span> {vci.youtube.top_recent_content}</p>}
                              {vci.youtube.content_themes && <p className="text-[#D4E2F0] text-sm mt-1"><span className="text-[#A3B5C7]">Themes:</span> {vci.youtube.content_themes}</p>}
                            </div>
                          )}
                          {vci.social_video && (typeof vci.social_video === 'object') && (
                            <div className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                              <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-3 font-medium">Social Video</h4>
                              <div className="space-y-2 text-sm">
                                {vci.social_video.instagram_reels && <div><span className="text-[#A3B5C7]">Instagram Reels:</span> <span className="text-[#D4E2F0] ml-1">{vci.social_video.instagram_reels}</span></div>}
                                {vci.social_video.tiktok && <div><span className="text-[#A3B5C7]">TikTok:</span> <span className="text-[#D4E2F0] ml-1">{vci.social_video.tiktok}</span></div>}
                                {vci.social_video.linkedin_video && <div><span className="text-[#A3B5C7]">LinkedIn Video:</span> <span className="text-[#D4E2F0] ml-1">{vci.social_video.linkedin_video}</span></div>}
                              </div>
                            </div>
                          )}
                          {vci.vimeo_or_other && <p className="text-[#D4E2F0] text-sm"><span className="text-[#A3B5C7]">Other Video:</span> {vci.vimeo_or_other}</p>}
                          {vci.quality_evidence && <p className="text-[#D4E2F0] text-sm"><span className="text-[#A3B5C7]">Quality Evidence:</span> {vci.quality_evidence}</p>}
                        </div>
                      ) : pacText ? <p className="text-[#D4E2F0] leading-relaxed whitespace-pre-wrap">{pacText}</p> : <EmptyPlaceholder />}
                    </CollapsibleSection>
                  </motion.div>
                );
              })()}

              {/* Marketing & Communications Strategy — removed (Part C) */}

              {/* 2. Mission Alignment (organisation) */}
              {(() => {
                const climate = firstNonEmpty(parsedResearch?.mission_alignment, parsedResearch?.climate_solutions_focus, parsedResearch?.sustainability_mandate);
                const climateRating = firstNonEmpty(climate?.rating, climate?.mission_alignment_rating, climate?.ocean_specificity_rating);
                const climateDetails = typeof climate === 'string' ? climate : climate?.details;
                return (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <CollapsibleSection id="sustainability" title="Mission Alignment" icon={Leaf} badge={climateRating && <RatingBadge rating={climateRating} />} editableKey="mission_alignment" editableValue={JSON.stringify(firstNonEmpty(parsedResearch?.mission_alignment, parsedResearch?.climate_solutions_focus, parsedResearch?.sustainability_mandate) || { rating: '', details: '' }, null, 2)}>
                    {climateDetails ? <p className="text-[#D4E2F0] leading-relaxed">{climateDetails}</p> : <EmptyPlaceholder />}
                  </CollapsibleSection>
                </motion.div>
                );
              })()}

              {/* Fit Assessment */}
              {(() => {
                const fit = parsedResearch?.fit_assessment ?? parsedResearch?.in_depth_fit_assessment;
                const isNewFit = fit && typeof fit === 'object' && (fit.prospective_client || fit.funder_investor || fit.multiplier || fit.strategic_connector);
                const NewFitRow = ({ label, value }) => value ? (
                  <div className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                    <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">{label}</h4>
                    <p className="text-[#D4E2F0] text-sm leading-relaxed">{value}</p>
                  </div>
                ) : null;
                const FitRow = ({ label, data }) => data ? (
                  <tr className="border-b border-[#1A2F4C]/50">
                    <td className="py-2.5 px-3 text-[#D4E2F0] text-sm font-medium">{label}</td>
                    <td className="py-2.5 px-3"><RatingBadge rating={data.rating} /></td>
                    <td className="py-2.5 px-3 text-[#A3B5C7] text-sm">{data.rationale}</td>
                  </tr>
                ) : null;
                // Data-driven label from a schema key: "intelligence_suite_fit" -> "Intelligence Suite Fit".
                const humanize = (k) => String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                    <CollapsibleSection id="fit_assessment" title="Fit Assessment" icon={Target} editableKey="fit_assessment" editableValue={typeof fit === 'string' ? fit : JSON.stringify(fit || '', null, 2)}>
                      {isNewFit ? (
                        <div className="space-y-3">
                          <NewFitRow label="Prospective Client" value={fit.prospective_client} />
                          <NewFitRow label="Funder / Investor" value={fit.funder_investor} />
                          <NewFitRow label="Multiplier" value={fit.multiplier} />
                          <NewFitRow label="Strategic Connector" value={fit.strategic_connector} />
                        </div>
                      ) : (fit && typeof fit === 'object') ? (
                        <div className="space-y-5">
                          {/* Fully data-driven: render whatever fit-group objects the record carries
                              (e.g. offering_fit, or a legacy intelligence_suite_fit), using their own
                              sub-key names as labels. No hardcoded stream/product names. */}
                          {Object.entries(fit).map(([groupKey, groupVal]) => {
                            if (!groupVal || typeof groupVal !== 'object') return null;
                            const rows = Object.entries(groupVal).filter(
                              ([, d]) => d && typeof d === 'object' && ('rating' in d || 'rationale' in d)
                            );
                            if (rows.length === 0) return null;
                            return (
                              <div key={groupKey}>
                                <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-2 font-medium">{humanize(groupKey)}</h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm"><tbody>
                                    {rows.map(([subKey, d]) => (
                                      <FitRow key={subKey} label={humanize(subKey)} data={d} />
                                    ))}
                                  </tbody></table>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <EmptyPlaceholder />}
                    </CollapsibleSection>
                  </motion.div>
                );
              })()}

              {/* Project Alignment — removed (Part C) */}

              {/* Funding Relationships & Capacity */}
              {(() => {
                const frc = parsedResearch?.funding_relationships_capacity;
                const mechanisms = firstNonEmpty(frc?.funding_mechanisms, parsedResearch?.philanthropic_mechanism);
                const sbs = parsedResearch?.scope_budget_signals;
                let capacity = frc?.capacity_signals;
                if (!capacity && sbs) {
                  capacity = typeof sbs === 'string' ? sbs : [sbs.likely_budget_range, sbs.decision_speed, sbs.procurement_type].filter(Boolean).join(' · ');
                }
                const hasContent = mechanisms || capacity;
                return (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <CollapsibleSection id="funding_capacity" title="Funding Relationships & Capacity" icon={DollarSign} editableKey="funding_relationships_capacity" editableValue={JSON.stringify(firstNonEmpty(parsedResearch?.funding_relationships_capacity, { funding_mechanisms: parsedResearch?.philanthropic_mechanism || '', capacity_signals: '' }), null, 2)}>
                      {hasContent ? (
                        <div className="space-y-3">
                          {mechanisms && (
                            <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                              <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">Funding Mechanisms</h4>
                              <p className="text-[#D4E2F0] text-sm leading-relaxed">{typeof mechanisms === 'string' ? mechanisms : JSON.stringify(mechanisms)}</p>
                            </div>
                          )}
                          {capacity && (
                            <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                              <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">Capacity Signals</h4>
                              <p className="text-[#D4E2F0] text-sm leading-relaxed">{typeof capacity === 'string' ? capacity : JSON.stringify(capacity)}</p>
                            </div>
                          )}
                        </div>
                      ) : <EmptyPlaceholder />}
                    </CollapsibleSection>
                  </motion.div>
                );
              })()}

              {/* 3. Current Activity Intelligence */}
              {(() => {
                const cai = parsedResearch?.current_activity_intelligence || target.current_activity_intelligence;
                return (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.34 }}>
                    <CollapsibleSection id="current_activity" title="Current Activity Intelligence" icon={TrendingUp} editableKey="current_activity_intelligence" editableValue={JSON.stringify(parsedResearch?.current_activity_intelligence || {}, null, 2)}>
                      {cai ? (
                        <div className="space-y-3">
                          {cai.active_projects && <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]"><h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1">Active Projects</h4><p className="text-[#D4E2F0] text-sm">{cai.active_projects}</p></div>}
                          {cai.recent_announcements && <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]"><h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1">Recent Announcements</h4><p className="text-[#D4E2F0] text-sm">{cai.recent_announcements}</p></div>}
                          {cai.recent_funding && <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]"><h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1">Recent Funding</h4><p className="text-[#D4E2F0] text-sm">{cai.recent_funding}</p></div>}
                          {cai.events_and_speaking && <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]"><h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1">Upcoming Events</h4><p className="text-[#D4E2F0] text-sm">{cai.events_and_speaking}</p></div>}
                          {cai.events_upcoming && <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]"><h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1">Upcoming Events</h4><p className="text-[#D4E2F0] text-sm">{cai.events_upcoming}</p></div>}
                          {cai.leadership_changes && <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]"><h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1">Leadership Changes</h4><p className="text-[#D4E2F0] text-sm">{cai.leadership_changes}</p></div>}
                          {Array.isArray(cai.press_coverage) && cai.press_coverage.length > 0 && (
                            <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                              <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-2">Press Coverage</h4>
                              <ul className="space-y-1">{cai.press_coverage.map((p, i) => <li key={i} className="text-[#D4E2F0] text-sm pl-3 border-l-2 border-[#1A2F4C]">{p}</li>)}</ul>
                            </div>
                          )}
                        </div>
                      ) : <EmptyPlaceholder />}
                    </CollapsibleSection>
                  </motion.div>
                );
              })()}

              {/* Screening Opportunity — removed (Part C) */}

              {/* 5. Reputational Risk Check */}
              {(() => {
                const aiRep = firstNonEmpty(parsedResearch?.reputational_risk_check, parsedResearch?.greenwash_check) || {};
                const eff = target.reputational_override != null ? target.reputational_override : aiRep;
                return (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }}>
                  <CollapsibleSection id="greenwash" title="Reputational Risk Check" icon={ShieldCheck} badge={eff?.verdict && <RatingBadge rating={eff.verdict} />}>
                    {renderReputationalOverride(aiRep, 'org')}
                  </CollapsibleSection>
                </motion.div>
                );
              })()}

              {/* Evidence of Engagement */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.385 }}>
                <CollapsibleSection id="org_evidence" title="Evidence of Engagement" icon={FileText} editableKey="evidence_of_engagement" editableValue={target.evidence_of_engagement}>
                  {(parsedResearch?.evidence_of_engagement || target.evidence_of_engagement) ? (
                    <p className="text-[#D4E2F0] leading-relaxed whitespace-pre-wrap">{parsedResearch?.evidence_of_engagement || target.evidence_of_engagement}</p>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Engagement Approach */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.39 }}>
                <CollapsibleSection id="org_engagement" title="Engagement Approach" icon={ArrowRight} editableKey="engagement_approach" editableValue={target.engagement_approach}>
                  {(parsedResearch?.engagement_approach || target.engagement_approach) ? (
                    <p className="text-[#D4E2F0] leading-relaxed whitespace-pre-wrap">{parsedResearch?.engagement_approach || target.engagement_approach}</p>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* 6. BD Angles */}
              {(() => {
                const angles = safeArray(parsedResearch?.bd_angles || target.bd_angles);
                return (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <CollapsibleSection id="bd_angles" title="BD Angles — Food for Thought" icon={Zap} editableKey="bd_angles" editableValue={angles}>
                      {angles.length > 0 ? (
                        <div className="space-y-3">
                          {angles.map((angle, i) => (
                            <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#00D4C8]/20 flex gap-3">
                              <span className="text-[#00D4C8] font-bold text-lg shrink-0">{i + 1}.</span>
                              <p className="text-[#D4E2F0] leading-relaxed text-sm">{angle}</p>
                            </div>
                          ))}
                        </div>
                      ) : <EmptyPlaceholder />}
                    </CollapsibleSection>
                  </motion.div>
                );
              })()}

              {/* 7. Pitch Hooks by Relationship Type */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}>
                <CollapsibleSection id="pitch_hooks" title="Suggested Pitch Hook" icon={Zap} editableKey="suggested_pitch_hook" editableValue={target.suggested_pitch_hook}>
                  {Array.isArray(parsedResearch?.suggested_pitch_hooks) && parsedResearch.suggested_pitch_hooks.length > 0 ? (
                    <div className="grid gap-3">
                      {parsedResearch.suggested_pitch_hooks.map((hook, i) => (
                        <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#00D4C8]/10 text-[#00D4C8] rounded border border-[#00D4C8]/20">{hook.relationship_type}</span>
                          </div>
                          <p className="text-[#D4E2F0] italic">"{hook.hook}"</p>
                        </div>
                      ))}
                    </div>
                  ) : target.suggested_pitch_hook ? (
                    <p className="text-[#D4E2F0] leading-relaxed italic border-l-2 border-[#00D4C8] pl-4">"{target.suggested_pitch_hook}"</p>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Legacy active_video_evidence now folded into Public Activity & Content (Part C) */}
            </>
          )}

          {/* ===== INDIVIDUAL SECTIONS ===== */}
          {!isFilm && isIndividual && (
            <>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <CollapsibleSection id="background" title="Professional Background" icon={User} editableKey="professional_background" editableValue={parsedResearch?.background}>
                  {parsedResearch?.background ? <p className="text-[#D4E2F0] leading-relaxed">{parsedResearch.background}</p> : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Career History */}
              {Array.isArray(parsedResearch?.all_current_roles) && parsedResearch.all_current_roles.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
                  <CollapsibleSection id="current_roles" title="Current Roles" icon={Building} editableKey="all_current_roles" editableValue={JSON.stringify(parsedResearch?.all_current_roles || [], null, 2)}>
                    <div className="space-y-3">
                      {parsedResearch.all_current_roles.map((role, i) => (
                        <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <h4 className="text-white font-medium text-sm">{role.title}</h4>
                              <p className="text-[#00D4C8] text-sm">{role.organisation}</p>
                            </div>
                            {role.org_funding_stage && (
                              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#1A2F4C] text-[#A3B5C7] rounded">{role.org_funding_stage}</span>
                            )}
                          </div>
                          {role.org_mission && <p className="text-[#A3B5C7] text-xs mt-1">{role.org_mission}</p>}
                          {role.org_relevance && <p className="text-[#D4E2F0] text-xs mt-2 pl-3 border-l-2 border-[#00D4C8]/30">{role.org_relevance}</p>}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {Array.isArray(parsedResearch?.career_history) && parsedResearch.career_history.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}>
                  <CollapsibleSection id="career_history" title="Career History" icon={BookOpen} editableKey="career_history" editableValue={JSON.stringify(parsedResearch?.career_history || [], null, 2)}>
                    <div className="space-y-2">
                      {parsedResearch.career_history.map((role, i) => (
                        <div key={i} className="flex gap-3 items-start group">
                          <div className="w-2 h-2 rounded-full bg-[#00D4C8]/60 mt-2 shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-baseline gap-2">
                              <span className="text-white text-sm font-medium">{role.role}</span>
                              <span className="text-[#A3B5C7] text-xs">at {role.organisation}</span>
                              {role.period && <span className="text-[#A3B5C7]/60 text-xs ml-auto">{role.period}</span>}
                            </div>
                            {role.key_achievement && <p className="text-[#D4E2F0] text-xs mt-0.5">{role.key_achievement}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Media Presence */}
              {parsedResearch?.media_presence && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}>
                  <CollapsibleSection id="media_presence" title="Media & Public Presence" icon={PlayCircle} editableKey="media_presence" editableValue={JSON.stringify(parsedResearch?.media_presence || {}, null, 2)}>
                    <div className="space-y-4">
                      {Array.isArray(parsedResearch.media_presence.podcast_appearances) && parsedResearch.media_presence.podcast_appearances.length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-2 font-medium">Podcast Appearances</h4>
                          <ul className="space-y-1">{parsedResearch.media_presence.podcast_appearances.map((p, i) => <li key={i} className="text-[#D4E2F0] text-sm pl-3 border-l-2 border-[#00D4C8]/30">{p}</li>)}</ul>
                        </div>
                      )}
                      {Array.isArray(parsedResearch.media_presence.conference_talks) && parsedResearch.media_presence.conference_talks.length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-2 font-medium">Conference & Speaking</h4>
                          <ul className="space-y-1">{parsedResearch.media_presence.conference_talks.map((c, i) => <li key={i} className="text-[#D4E2F0] text-sm pl-3 border-l-2 border-[#1A2F4C]">{c}</li>)}</ul>
                        </div>
                      )}
                      {Array.isArray(parsedResearch.media_presence.published_writing) && parsedResearch.media_presence.published_writing.length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] mb-2 font-medium">Published Writing</h4>
                          <ul className="space-y-1">{parsedResearch.media_presence.published_writing.map((w, i) => <li key={i} className="text-[#D4E2F0] text-sm pl-3 border-l-2 border-[#1A2F4C]">{w}</li>)}</ul>
                        </div>
                      )}
                      {Array.isArray(parsedResearch.media_presence.social_platforms) && parsedResearch.media_presence.social_platforms.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {parsedResearch.media_presence.social_platforms.map((p, i) => (
                            <span key={i} className="px-2 py-1 text-xs bg-[#1A2F4C] text-[#D4E2F0] rounded">{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Mission Alignment */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <CollapsibleSection id="mission_alignment" title="Mission Alignment" icon={Leaf} badge={(parsedResearch?.mission_alignment_rating || parsedResearch?.ocean_connection_rating) && <RatingBadge rating={parsedResearch.mission_alignment_rating || parsedResearch.ocean_connection_rating} />} editableKey="mission_alignment" editableValue={JSON.stringify({ rating: parsedResearch?.mission_alignment_rating || parsedResearch?.ocean_connection_rating || '', details: parsedResearch?.mission_alignment_details || parsedResearch?.ocean_connection_details || '' }, null, 2)}>
                  {(parsedResearch?.mission_alignment_details || parsedResearch?.ocean_connection_details) ? (
                    <div>
                      <p className="text-[#D4E2F0] leading-relaxed">{parsedResearch.mission_alignment_details || parsedResearch.ocean_connection_details}</p>
                      {parsedResearch.estimated_giving_capacity && (
                        <div className="mt-4 p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] inline-block">
                          <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">Estimated Giving Capacity: </span>
                          <span className="text-[#00D4C8] font-medium">{parsedResearch.estimated_giving_capacity}</span>
                        </div>
                      )}
                    </div>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Estimated Giving Capacity (separate if not in mission alignment) */}
              {!(parsedResearch?.mission_alignment_details || parsedResearch?.ocean_connection_details) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}>
                  <CollapsibleSection id="giving_capacity" title="Estimated Giving Capacity" icon={DollarSign}>
                    {parsedResearch?.estimated_giving_capacity ? (
                      <p className="text-[#00D4C8] font-medium text-lg">{parsedResearch.estimated_giving_capacity}</p>
                    ) : <EmptyPlaceholder />}
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Evidence of Engagement */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <CollapsibleSection id="evidence" title="Evidence of Engagement" icon={Check} editableKey="evidence_items" editableValue={JSON.stringify(parsedResearch?.evidence_items || [], null, 2)}>
                  {Array.isArray(parsedResearch?.evidence_items) && parsedResearch.evidence_items.length > 0 ? (
                    <ul className="space-y-2">
                      {parsedResearch.evidence_items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-[#D4E2F0]">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00D4C8] mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Engagement Approach */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                <CollapsibleSection id="engagement" title="Engagement Approach" icon={Zap} editableKey="engagement_approach" editableValue={parsedResearch?.engagement_approach}>
                  {parsedResearch?.engagement_approach ? (
                    <p className="text-[#D4E2F0] leading-relaxed italic border-l-2 border-[#00D4C8] pl-4">{parsedResearch.engagement_approach}</p>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Red Flag Check */}
              {(() => {
                const aiRep = parsedResearch?.red_flag_check || {};
                const eff = target.reputational_override != null ? target.reputational_override : aiRep;
                return (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <CollapsibleSection id="red_flag" title="Red Flag Check" icon={AlertTriangle} badge={eff?.verdict && <RatingBadge rating={eff.verdict} />}>
                    {renderReputationalOverride(aiRep, 'ind')}
                  </CollapsibleSection>
                </motion.div>
                );
              })()}

              {/* Awards & Recognition */}
              {Array.isArray(parsedResearch?.awards_and_recognition) && parsedResearch.awards_and_recognition.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.146 }}>
                  <CollapsibleSection id="awards" title="Awards & Recognition" icon={Award}>
                    <ul className="space-y-2">
                      {parsedResearch.awards_and_recognition.map((award, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#D4E2F0]">
                          <Star className="w-4 h-4 text-[#00D4C8] mt-0.5 shrink-0" strokeWidth={1.5} />
                          <span className="text-sm">{award}</span>
                        </li>
                      ))}
                    </ul>
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* BD Angles for Individuals */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                <CollapsibleSection id="bd_angles" title="BD Angles — Food for Thought" icon={Zap} editableKey="bd_angles" editableValue={parsedResearch?.bd_angles}>
                  {Array.isArray(parsedResearch?.bd_angles) && parsedResearch.bd_angles.length > 0 ? (
                    <div className="space-y-3">
                      {parsedResearch.bd_angles.map((angle, i) => (
                        <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#00D4C8]/20 flex gap-3">
                          <span className="text-[#00D4C8] font-bold text-lg shrink-0">{i + 1}.</span>
                          <p className="text-[#D4E2F0] leading-relaxed text-sm">{angle}</p>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>

              {/* Pitch Hooks for Individuals */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }}>
                <CollapsibleSection id="pitch_hooks" title="Suggested Pitch Hook" icon={Target} editableKey="suggested_pitch_hook" editableValue={target.suggested_pitch_hook}>
                  {Array.isArray(parsedResearch?.suggested_pitch_hooks) && parsedResearch.suggested_pitch_hooks.length > 0 ? (
                    <div className="space-y-3">
                      {parsedResearch.suggested_pitch_hooks.map((hook, i) => (
                        <div key={i} className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                          <span className="text-xs uppercase tracking-wider text-[#00D4C8] font-medium">{hook.relationship_type}</span>
                          <p className="text-[#D4E2F0] text-sm mt-1">{hook.hook}</p>
                        </div>
                      ))}
                    </div>
                  ) : target.suggested_pitch_hook ? (
                    <p className="text-[#D4E2F0] leading-relaxed italic border-l-2 border-[#00D4C8] pl-4">"{target.suggested_pitch_hook}"</p>
                  ) : <EmptyPlaceholder />}
                </CollapsibleSection>
              </motion.div>
            </>
          )}

          {/* ===== SHARED SECTIONS ===== */}

          {/* Key Contacts */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <CollapsibleSection id="contacts" title="Key Contacts" icon={Users}>
              {target.key_contacts?.length > 0 ? (
                <div className="space-y-3">
                  {target.key_contacts.map((contact, index) => (
                    <div key={index} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid={`contact-${index}`}>
                      {editingContact === index ? (
                        /* ---- EDIT MODE ---- */
                        <div className="space-y-3" data-testid={`contact-edit-${index}`}>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Name</label>
                              <input
                                value={contactDraft.name}
                                onChange={(e) => setContactDraft(d => ({ ...d, name: e.target.value }))}
                                className="w-full px-3 py-2 rounded bg-[#0E1C33] border border-[#1A2F4C] text-white text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors"
                                data-testid={`contact-edit-name-${index}`}
                              />
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Role / Title</label>
                              <input
                                value={contactDraft.title}
                                onChange={(e) => setContactDraft(d => ({ ...d, title: e.target.value }))}
                                className="w-full px-3 py-2 rounded bg-[#0E1C33] border border-[#1A2F4C] text-white text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors"
                                data-testid={`contact-edit-role-${index}`}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Email</label>
                              <input
                                type="email"
                                value={contactDraft.email}
                                onChange={(e) => setContactDraft(d => ({ ...d, email: e.target.value }))}
                                placeholder="name@example.com"
                                className="w-full px-3 py-2 rounded bg-[#0E1C33] border border-[#1A2F4C] text-white text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors placeholder:text-[#A3B5C7]/40"
                                data-testid={`contact-edit-email-${index}`}
                              />
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">LinkedIn URL</label>
                              <input
                                value={contactDraft.linkedin}
                                onChange={(e) => setContactDraft(d => ({ ...d, linkedin: e.target.value }))}
                                placeholder="https://linkedin.com/in/..."
                                className="w-full px-3 py-2 rounded bg-[#0E1C33] border border-[#1A2F4C] text-white text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors placeholder:text-[#A3B5C7]/40"
                                data-testid={`contact-edit-linkedin-${index}`}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 block">Notes</label>
                            <textarea
                              value={contactDraft.notes}
                              onChange={(e) => setContactDraft(d => ({ ...d, notes: e.target.value }))}
                              rows={2}
                              className="w-full px-3 py-2 rounded bg-[#0E1C33] border border-[#1A2F4C] text-white text-sm resize-none focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors"
                              data-testid={`contact-edit-notes-${index}`}
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => { setEditingContact(null); setContactDraft({}); }}
                              className="px-3 py-1.5 text-xs text-[#A3B5C7] hover:text-white transition-colors"
                              data-testid={`contact-cancel-${index}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveContact}
                              disabled={savingContact}
                              className="px-4 py-1.5 text-xs bg-[#00D4C8] text-[#0A1628] rounded font-medium hover:bg-[#33DDD3] transition-colors flex items-center gap-1.5"
                              data-testid={`contact-save-${index}`}
                            >
                              {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ---- VIEW MODE ---- */
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium">{contact.name}</p>
                            <p className="text-sm text-[#00D4C8]">{contact.title || contact.role}</p>
                            {contact.email && (
                              <a
                                href={`mailto:${contact.email}`}
                                className="text-sm text-[#A3B5C7] hover:text-[#00D4C8] transition-colors flex items-center gap-1.5 mt-1"
                                data-testid={`contact-email-${index}`}
                              >
                                <Mail className="w-3 h-3" strokeWidth={1.5} />
                                {contact.email}
                              </a>
                            )}
                            {contact.notes && <p className="text-sm text-[#A3B5C7] mt-2">{contact.notes}</p>}
                            {contact.owner && <span className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mt-1 block">Owner: {contact.owner}</span>}
                          </div>
                          <div className="flex items-center gap-2 ml-3 shrink-0">
                            <button
                              onClick={() => handleEditContact(index)}
                              className="p-1.5 rounded hover:bg-[#1A2F4C] text-[#A3B5C7] hover:text-[#00D4C8] transition-colors"
                              title="Edit contact"
                              data-testid={`contact-edit-btn-${index}`}
                            >
                              <Pencil className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            {(contact.linkedin || contact.linkedin_url) && (
                              <a href={contact.linkedin || contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-[#1A2F4C] text-[#A3B5C7] hover:text-[#00D4C8] transition-colors" title="LinkedIn">
                                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : <EmptyPlaceholder />}
            </CollapsibleSection>
          </motion.div>

          {/* Suggested Pitch Hook (shared fallback - only for non-org/non-individual or when no hooks above) */}
          {!isIndividual && !parsedResearch?.suggested_pitch_hooks?.length && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
              <CollapsibleSection id="pitch" title="Suggested Pitch Hook" icon={Zap} editableKey="suggested_pitch_hook" editableValue={target.suggested_pitch_hook}>
                {target.suggested_pitch_hook ? (
                  <p className="text-[#D4E2F0] leading-relaxed italic border-l-2 border-[#00D4C8] pl-4">"{target.suggested_pitch_hook}"</p>
                ) : <EmptyPlaceholder />}
              </CollapsibleSection>
            </motion.div>
          )}

          {/* Relevance Rationale */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <CollapsibleSection id="relevance" title="Relevance Rationale" icon={Target}>
              {parsedResearch?.relevance_rationale ? (
                <p className="text-[#D4E2F0] leading-relaxed">{parsedResearch.relevance_rationale}</p>
              ) : <EmptyPlaceholder />}
            </CollapsibleSection>
          </motion.div>

          {/* Relevant Projects */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
            <CollapsibleSection id="relevance_projects" title="Relevant Projects" icon={Star}>
              {target.relevant_projects?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {target.relevant_projects.map((projectId) => (
                    <span key={projectId} className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm">{getProjectName(projectId)}</span>
                  ))}
                </div>
              ) : <EmptyPlaceholder />}
            </CollapsibleSection>
          </motion.div>
        </div>

        {/* ===== TARGET-SPECIFIC CONTEXT IMPORT ===== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="mt-6">
          <div className="card-surface" data-testid="target-context-import">
            <button
              onClick={() => {
                const scrollBefore = window.scrollY;
                setContextOpen(!contextOpen);
                setContextApplied(false);
                if (!contextOpen) {
                  // Prevent scroll jump when section opens
                  requestAnimationFrame(() => {
                    window.scrollTo(0, scrollBefore);
                  });
                }
              }}
              className="w-full p-6 flex items-center justify-between text-left"
              data-testid="context-import-toggle"
            >
              <div className="flex items-center gap-3">
                <Upload className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
                <h3 className="font-heading text-xl text-white">Import Context for {target.name}</h3>
              </div>
              {contextOpen ? <ChevronUp className="w-5 h-5 text-[#A3B5C7]" /> : <ChevronDown className="w-5 h-5 text-[#A3B5C7]" />}
            </button>
            
            {contextOpen && (
                  <div className="px-6 pb-6 space-y-4">
                    {!contextPreview ? (
                      <>
                        <p className="text-sm text-[#A3B5C7]">
                          Paste call notes, emails, or meeting minutes about {target.name}. The AI will extract proposed updates for review.
                        </p>

                        {/* Content Type Selector */}
                        <div>
                          <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block font-medium">Content Type</label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { key: 'email_thread', label: 'Email Thread' },
                              { key: 'whatsapp', label: 'WhatsApp' },
                              { key: 'meeting_notes', label: 'Meeting Notes' },
                              { key: 'call_notes', label: 'Call Notes' },
                              { key: 'article', label: 'Article / Research' },
                              { key: 'other', label: 'Other' },
                            ].map(t => (
                              <button
                                key={t.key}
                                onClick={() => setContextContentType(t.key)}
                                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${contextContentType === t.key ? 'bg-[#00D4C8]/10 border-[#00D4C8] text-[#00D4C8]' : 'bg-[#0A1628] border-[#1A2F4C] text-[#A3B5C7] hover:border-[#00D4C8]/30'}`}
                                data-testid={`content-type-${t.key}`}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Focus selector */}
                        <div className="flex flex-wrap gap-2">
                          {[
                            { key: 'all', label: 'All Updates' },
                            { key: 'pipeline_stage', label: 'Pipeline Stage' },
                            { key: 'contact_notes', label: 'Notes & Quotes' },
                            { key: 'pitch_hook', label: 'Pitch Hook' },
                            { key: 'key_contacts', label: 'Key Contacts' },
                          ].map(f => (
                            <button
                              key={f.key}
                              onClick={() => setContextFocus(f.key)}
                              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${contextFocus === f.key ? 'bg-[#00D4C8]/10 border-[#00D4C8] text-[#00D4C8]' : 'bg-[#0A1628] border-[#1A2F4C] text-[#A3B5C7] hover:border-[#00D4C8]/30'}`}
                              data-testid={`context-focus-${f.key}`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                        
                        <textarea
                          value={contextText}
                          onChange={(e) => setContextText(e.target.value)}
                          rows={8}
                          placeholder="Paste your call notes, emails, WhatsApp messages, or meeting minutes here..."
                          className="w-full p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm placeholder:text-[#A3B5C7]/50 focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors resize-none"
                          data-testid="context-import-textarea"
                        />

                        {/* File Upload */}
                        <div className="space-y-2 -mt-1">
                          <input
                            ref={ctxFileInputRef}
                            type="file"
                            accept=".pdf,.docx,.txt,.jpg,.jpeg,.png"
                            onChange={handleCtxFileSelect}
                            className="hidden"
                            data-testid="target-file-upload-input"
                          />
                          {!ctxFile ? (
                            <button
                              type="button"
                              onClick={() => ctxFileInputRef.current?.click()}
                              disabled={ctxFileExtracting}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-[#1A2F4C] hover:border-[#00D4C8]/40 text-[#A3B5C7] hover:text-white transition-all text-xs"
                              data-testid="target-upload-file-btn"
                            >
                              <FileIcon className="w-3.5 h-3.5" />
                              Upload File
                              <span className="text-[#A3B5C7]/50 ml-1">(PDF, DOCX, TXT, JPG, PNG)</span>
                            </button>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                              {ctxFile.name.match(/\.(jpg|jpeg|png)$/i) ? (
                                <Image className="w-3.5 h-3.5 text-[#00D4C8] flex-shrink-0" />
                              ) : (
                                <FileText className="w-3.5 h-3.5 text-[#00D4C8] flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-xs truncate">{ctxFile.name}</p>
                                <p className="text-[#A3B5C7]/60 text-[10px]">
                                  {fmtSize(ctxFile.size)}
                                  {ctxFileExtracting && ' — extracting text...'}
                                  {ctxFileText && ` — ${ctxFileText.length.toLocaleString()} chars extracted`}
                                </p>
                              </div>
                              {ctxFileExtracting ? (
                                <Loader2 className="w-3.5 h-3.5 text-[#00D4C8] animate-spin flex-shrink-0" />
                              ) : ctxFileText ? (
                                <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                              ) : null}
                              <button onClick={removeCtxFile} className="p-0.5 rounded hover:bg-white/10 text-[#A3B5C7] hover:text-white" data-testid="target-remove-file-btn">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          {ctxFileError && (
                            <div className="flex items-center gap-1.5 text-red-400 text-xs" data-testid="target-file-error">
                              <AlertCircle className="w-3 h-3" />
                              {ctxFileError}
                            </div>
                          )}
                        </div>

                        <p className="text-xs text-[#A3B5C7]/60 -mt-1">You can paste notes, thoughts or observations in any format — BREACH will extract and apply the relevant intelligence.</p>
                        
                        <button
                          onClick={async () => {
                            const combinedCtx = [ctxFileText, contextText].filter(Boolean).join('\n\n--- Additional Context ---\n\n');
                            if (!combinedCtx.trim()) return;
                            setContextParsing(true);
                            setContextApplied(false);
                            try {
                              // Start async job
                              const startRes = await targetsApi.parseContext(id, combinedCtx.trim(), contextFocus, contextContentType);
                              const jobId = startRes.data?.job_id;
                              if (!jobId) throw new Error('No job_id returned');

                              // Poll for completion
                              let result = null;
                              await pollJob({
                                fetchStatus: () => targetsApi.parseContextStatus(id, jobId).then(r => r.data),
                                isComplete: (d) => d.status === 'complete' && d.result,
                                isError: (d) => d.status === 'error' || d.status === 'expired',
                                getError: (d) => d.error || 'Context parsing failed',
                                onComplete: (d) => { result = d.result; },
                                onError: (msg) => { throw new Error(msg); },
                                intervalMs: 2000,
                                maxPolls: 90,
                              });
                              if (!result) throw new Error('Context parsing timed out');

                              setContextPreview(result);
                              // Auto-select all non-null proposed updates
                              const sel = {};
                              const pu = result?.parsed?.proposed_updates;
                              if (pu) {
                                if (pu.pipeline_stage) sel.pipeline_stage = true;
                                if (pu.temperature) sel.temperature = true;
                                if (pu.last_contact_date) sel.last_contact_date = true;
                                if (pu.next_action) sel.next_action = true;
                                if (pu.next_action_due) sel.next_action_due = true;
                                if (pu.pitch_hook_update) sel.pitch_hook_update = true;
                                if (pu.mandate_update) sel.mandate_update = true;
                                if (pu.new_contacts?.length > 0) sel.new_contacts = true;
                                if (pu.key_notes?.length > 0) sel.key_notes = true;
                                if (pu.relevant_projects?.length > 0) sel.relevant_projects = true;
                                if (pu.forward_looking_opportunities?.length > 0) sel.forward_looking_opportunities = true;
                                if (pu.re_engagement_signal?.should_flag) sel.re_engagement_signal = true;
                                if (pu.bd_angles?.length > 0) sel.bd_angles = true;
                                if (pu.card_field_updates?.length > 0) {
                                  pu.card_field_updates.forEach((_, i) => { sel[`card_field_${i}`] = true; });
                                }
                              }
                              setContextSelected(sel);
                              setTimeout(() => {
                                previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }, 100);
                            } catch (err) {
                              console.error('Context parse error:', err);
                              toast.error(err.message || 'Context parsing failed. Please try again with a shorter text.');
                            } finally {
                              setContextParsing(false);
                            }
                          }}
                          disabled={contextParsing || (!contextText.trim() && !ctxFileText.trim()) || ctxFileExtracting}
                          className="btn-primary flex items-center gap-2"
                          data-testid="context-import-parse-btn"
                        >
                          {contextParsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                          {contextParsing ? 'Extracting...' : 'Preview Proposed Updates'}
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Preview Panel */}
                        <div ref={previewRef} className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#00D4C8]" strokeWidth={1.5} />
                            <h4 className="text-sm font-medium text-white">Proposed Updates for {target.name}</h4>
                            {contextPreview?.parsed?.confidence && (
                              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${
                                contextPreview.parsed.confidence === 'HIGH' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                                contextPreview.parsed.confidence === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                                'bg-orange-500/10 text-orange-400 border-orange-500/20'
                              }`}>
                                {contextPreview.parsed.confidence} confidence
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => { setContextPreview(null); setContextSelected({}); }}
                            className="text-[#A3B5C7] hover:text-white text-xs flex items-center gap-1"
                          >
                            <X className="w-3 h-3" strokeWidth={1.5} /> Back to notes
                          </button>
                        </div>
                        
                        {contextPreview?.parsed?.summary && (
                          <p className="text-sm text-[#D4E2F0] italic border-l-2 border-[#00D4C8] pl-3 mb-4">{contextPreview.parsed.summary}</p>
                        )}
                        
                        {/* Selectable updates */}
                        <div className="space-y-3" data-testid="context-preview-updates">
                          {(() => {
                            const pu = contextPreview?.parsed?.proposed_updates;
                            if (!pu) return <p className="text-sm text-[#A3B5C7]">No updates extracted.</p>;
                            const items = [];
                            
                            if (pu.pipeline_stage) items.push({ key: 'pipeline_stage', label: 'Pipeline Stage', value: pu.pipeline_stage, type: 'badge' });
                            if (pu.temperature) items.push({ key: 'temperature', label: 'Temperature', value: pu.temperature, type: 'badge' });
                            if (pu.last_contact_date) items.push({ key: 'last_contact_date', label: 'Last Contact', value: pu.last_contact_date, type: 'text' });
                            if (pu.next_action) items.push({ key: 'next_action', label: 'Next Action', value: pu.next_action, type: 'text' });
                            if (pu.next_action_due) items.push({ key: 'next_action_due', label: 'Next Action Due', value: pu.next_action_due, type: 'text' });
                            if (pu.pitch_hook_update) items.push({ key: 'pitch_hook_update', label: 'Pitch Hook Update', value: pu.pitch_hook_update, type: 'long' });
                            if (pu.mandate_update) items.push({ key: 'mandate_update', label: 'Mandate Update', value: pu.mandate_update, type: 'long' });
                            
                            return (
                              <>
                                {items.map(item => (
                                  <label key={item.key} className="flex items-start gap-3 p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] cursor-pointer hover:border-[#00D4C8]/30 transition-colors" data-testid={`context-update-${item.key}`}>
                                    <input
                                      type="checkbox"
                                      checked={!!contextSelected[item.key]}
                                      onChange={(e) => setContextSelected(prev => ({ ...prev, [item.key]: e.target.checked }))}
                                      className="mt-1 accent-[#00D4C8]"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">{item.label}</span>
                                      {item.type === 'badge' ? (
                                        <div className="mt-1">
                                          <span className="px-2 py-0.5 text-xs uppercase bg-[#00D4C8]/10 text-[#00D4C8] rounded border border-[#00D4C8]/20">{item.value}</span>
                                        </div>
                                      ) : item.type === 'long' ? (
                                        <p className="text-sm text-[#D4E2F0] mt-1">{item.value}</p>
                                      ) : (
                                        <p className="text-sm text-white mt-1">{item.value}</p>
                                      )}
                                    </div>
                                  </label>
                                ))}
                                
                                {pu.new_contacts?.length > 0 && (
                                  <label className="flex items-start gap-3 p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] cursor-pointer hover:border-[#00D4C8]/30 transition-colors" data-testid="context-update-new_contacts">
                                    <input
                                      type="checkbox"
                                      checked={!!contextSelected.new_contacts}
                                      onChange={(e) => setContextSelected(prev => ({ ...prev, new_contacts: e.target.checked }))}
                                      className="mt-1 accent-[#00D4C8]"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">New Contacts ({pu.new_contacts.length})</span>
                                      <div className="mt-2 space-y-2">
                                        {pu.new_contacts.map((c, i) => (
                                          <div key={i} className="text-sm">
                                            <div className="flex items-center gap-2">
                                              <User className="w-3.5 h-3.5 text-[#00D4C8]" strokeWidth={1.5} />
                                              <span className="text-white">{c.name}</span>
                                              {c.title && <span className="text-[#A3B5C7]">— {c.title}</span>}
                                              {c.needs_followup && <span className="px-1.5 py-0.5 text-[9px] uppercase bg-amber-500/10 text-amber-400 rounded">needs follow-up</span>}
                                            </div>
                                            {c.email && <p className="text-[#00D4C8] text-xs ml-5 mt-0.5">{c.email}</p>}
                                            {c.notes && <p className="text-[#A3B5C7] text-xs ml-5 mt-0.5">{c.notes}</p>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </label>
                                )}
                                
                                {pu.key_notes?.length > 0 && (
                                  <label className="flex items-start gap-3 p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] cursor-pointer hover:border-[#00D4C8]/30 transition-colors" data-testid="context-update-key_notes">
                                    <input
                                      type="checkbox"
                                      checked={!!contextSelected.key_notes}
                                      onChange={(e) => setContextSelected(prev => ({ ...prev, key_notes: e.target.checked }))}
                                      className="mt-1 accent-[#00D4C8]"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">Key Notes ({pu.key_notes.length})</span>
                                      <div className="mt-2 space-y-2">
                                        {pu.key_notes.map((n, i) => (
                                          <p key={i} className="text-sm text-[#D4E2F0] flex items-start gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#00D4C8] mt-2 flex-shrink-0" />
                                            {n.text}
                                          </p>
                                        ))}
                                      </div>
                                    </div>
                                  </label>
                                )}

                                {/* Card Field Updates — individual field changes */}
                                {pu.card_field_updates?.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-xs uppercase tracking-wider text-[#00D4C8] font-medium px-1">Proposed Card Field Changes</p>
                                    {pu.card_field_updates.map((cfu, i) => {
                                      const fieldLabels = {
                                        mission_alignment_rating: 'Mission Alignment Rating',
                                        mission_alignment_details: 'Mission Alignment Details',
                                        all_current_roles: 'Current Roles',
                                        career_history: 'Career History',
                                        evidence_items: 'Evidence of Engagement',
                                        engagement_approach: 'Engagement Approach',
                                        background: 'Professional Background',
                                        estimated_giving_capacity: 'Giving Capacity',
                                        relationship_types: 'Relationship Types',
                                        red_flag_check: 'Red Flag Check',
                                        reputational_risk_check: 'Reputational Risk Check',
                                        mission_programmes: 'Mission & Programmes',
                                        climate_solutions_focus: 'Climate Solutions Focus',
                                        funding_relationships_capacity: 'Funding Relationships & Capacity',
                                        public_activity_content: 'Public Activity & Content',
                                        awards_and_recognition: 'Awards',
                                      };
                                      const label = fieldLabels[cfu.field] || cfu.field;
                                      const actionLabel = cfu.action === 'add' ? 'Add to' : 'Update';
                                      return (
                                        <label key={i} className="flex items-start gap-3 p-4 rounded-lg bg-[#00D4C8]/5 border border-[#00D4C8]/20 cursor-pointer hover:border-[#00D4C8]/40 transition-colors" data-testid={`context-update-card-field-${i}`}>
                                          <input
                                            type="checkbox"
                                            checked={!!contextSelected[`card_field_${i}`]}
                                            onChange={(e) => setContextSelected(prev => ({ ...prev, [`card_field_${i}`]: e.target.checked }))}
                                            className="mt-1 accent-[#00D4C8]"
                                          />
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#00D4C8]/10 text-[#00D4C8] rounded border border-[#00D4C8]/20">{actionLabel}</span>
                                              <span className="text-xs uppercase tracking-wider text-white font-medium">{label}</span>
                                            </div>
                                            <div className="text-sm text-[#D4E2F0] mt-1">
                                              {typeof cfu.value === 'object' ? (
                                                <pre className="text-xs bg-[#0A1628] p-2 rounded overflow-x-auto">{JSON.stringify(cfu.value, null, 2)}</pre>
                                              ) : (
                                                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded text-xs border border-green-500/20">{String(cfu.value)}</span>
                                              )}
                                            </div>
                                            {cfu.reason && <p className="text-xs text-[#A3B5C7] mt-1 italic">{cfu.reason}</p>}
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Forward-Looking BD Opportunities */}
                                {pu.forward_looking_opportunities?.length > 0 && (
                                  <label className="flex items-start gap-3 p-4 rounded-lg bg-[#00D4C8]/5 border border-[#00D4C8]/20 cursor-pointer hover:border-[#00D4C8]/40 transition-colors" data-testid="context-update-forward_looking">
                                    <input
                                      type="checkbox"
                                      checked={!!contextSelected.forward_looking_opportunities}
                                      onChange={(e) => setContextSelected(prev => ({ ...prev, forward_looking_opportunities: e.target.checked }))}
                                      className="mt-1 accent-[#00D4C8]"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs uppercase tracking-wider text-[#00D4C8] font-medium">Forward-Looking BD Opportunities</span>
                                      <div className="mt-2 space-y-2">
                                        {pu.forward_looking_opportunities.map((flo, i) => (
                                          <div key={i} className="flex items-start gap-2 text-sm">
                                            <Zap className="w-3.5 h-3.5 text-[#00D4C8] mt-0.5 shrink-0" strokeWidth={1.5} />
                                            <div>
                                              <span className="text-white font-medium">{flo.project_name}</span>
                                              <p className="text-[#D4E2F0] text-sm mt-0.5">{flo.opportunity}</p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </label>
                                )}

                                {/* Re-engagement Signal */}
                                {pu.re_engagement_signal?.should_flag && (
                                  <label className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 cursor-pointer hover:border-amber-500/40 transition-colors" data-testid="context-update-re_engagement">
                                    <input
                                      type="checkbox"
                                      checked={!!contextSelected.re_engagement_signal}
                                      onChange={(e) => setContextSelected(prev => ({ ...prev, re_engagement_signal: e.target.checked }))}
                                      className="mt-1 accent-amber-400"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs uppercase tracking-wider text-amber-400 font-medium">Re-engagement Flag</span>
                                      <div className="mt-2 space-y-1 text-sm">
                                        {pu.re_engagement_signal.re_engage_after && (
                                          <p className="text-[#D4E2F0]"><span className="text-[#A3B5C7]">Re-engage after:</span> {pu.re_engagement_signal.re_engage_after}</p>
                                        )}
                                        {pu.re_engagement_signal.trigger && (
                                          <p className="text-[#D4E2F0]"><span className="text-[#A3B5C7]">Trigger:</span> {pu.re_engagement_signal.trigger}</p>
                                        )}
                                      </div>
                                    </div>
                                  </label>
                                )}
                                
                                {items.length === 0 && !pu.new_contacts?.length && !pu.key_notes?.length && !pu.card_field_updates?.length && !pu.forward_looking_opportunities?.length && !pu.re_engagement_signal?.should_flag && (
                                  <p className="text-sm text-[#A3B5C7]">No actionable updates found in the pasted notes.</p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        
                        {/* Apply / Discard buttons */}
                        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#1A2F4C]">
                          <button
                            onClick={async () => {
                              setContextApplying(true);
                              try {
                                const pu = contextPreview?.parsed?.proposed_updates || {};
                                const payload = {};
                                if (contextSelected.pipeline_stage) payload.pipeline_stage = pu.pipeline_stage;
                                if (contextSelected.temperature) payload.temperature = pu.temperature;
                                if (contextSelected.last_contact_date) payload.last_contact_date = pu.last_contact_date;
                                if (contextSelected.next_action) payload.next_action = pu.next_action;
                                if (contextSelected.next_action_due) payload.next_action_due = pu.next_action_due;
                                if (contextSelected.pitch_hook_update) payload.pitch_hook_update = pu.pitch_hook_update;
                                if (contextSelected.mandate_update) payload.mandate_update = pu.mandate_update;
                                if (contextSelected.new_contacts) payload.new_contacts = pu.new_contacts || [];
                                if (contextSelected.key_notes) payload.key_notes = pu.key_notes || [];
                                if (contextSelected.relevant_projects) payload.relevant_projects = pu.relevant_projects || [];
                                if (contextSelected.forward_looking_opportunities) payload.forward_looking_opportunities = pu.forward_looking_opportunities || [];
                                if (contextSelected.re_engagement_signal) payload.re_engagement_signal = pu.re_engagement_signal;
                                if (contextSelected.bd_angles) payload.bd_angles = pu.bd_angles || [];
                                // Always include contact_info_updates if present
                                if (pu.contact_info_updates) payload.contact_info_updates = pu.contact_info_updates;
                                // Collect selected card_field_updates
                                if (pu.card_field_updates?.length > 0) {
                                  payload.card_field_updates = pu.card_field_updates.filter((_, i) => contextSelected[`card_field_${i}`]);
                                }
                                
                                await targetsApi.applyContext(id, payload);
                                setContextApplied(true);
                                // Refresh target data
                                const refreshed = await targetsApi.getOne(id);
                                setTarget(refreshed.data);
                                // Reset after 3 seconds
                                setTimeout(() => {
                                  setContextPreview(null);
                                  setContextText('');
                                  setContextSelected({});
                                  setContextApplied(false);
                                }, 3000);
                              } catch (err) {
                                handleApiError(err, 'Failed to apply context');
                              } finally {
                                setContextApplying(false);
                              }
                            }}
                            disabled={contextApplying || contextApplied || Object.values(contextSelected).filter(Boolean).length === 0}
                            className="btn-primary flex items-center gap-2"
                            data-testid="context-apply-btn"
                          >
                            {contextApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : contextApplied ? <Check className="w-4 h-4" strokeWidth={2} /> : <Check className="w-4 h-4" strokeWidth={2} />}
                            {contextApplying ? 'Applying...' : contextApplied ? 'Applied Successfully' : `Apply ${Object.values(contextSelected).filter(Boolean).length} Updates`}
                          </button>
                          <button
                            onClick={() => { setContextPreview(null); setContextSelected({}); }}
                            className="btn-secondary flex items-center gap-2"
                            data-testid="context-discard-btn"
                          >
                            <X className="w-4 h-4" strokeWidth={1.5} />
                            Discard All
                          </button>
                        </div>
                      </>
                    )}
                  </div>
              )}
          </div>
        </motion.div>

        {/* ===== CONTEXT IMPORT LOG ===== */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="mt-4">
          <div className="card-surface" data-testid="context-import-log">
            <button
              onClick={() => setImportLogOpen(!importLogOpen)}
              className="w-full p-6 flex items-center justify-between text-left"
              data-testid="import-log-toggle"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#A3B5C7]" strokeWidth={1.5} />
                <h3 className="font-heading text-lg text-white">Import History</h3>
                {importLog.length > 0 && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-white/10 text-[#A3B5C7] rounded">{importLog.length}</span>}
              </div>
              {importLogOpen ? <ChevronUp className="w-5 h-5 text-[#A3B5C7]" /> : <ChevronDown className="w-5 h-5 text-[#A3B5C7]" />}
            </button>
            <AnimatePresence>
              {importLogOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 space-y-3">
                    {importLog.length > 0 ? importLog.map((entry, i) => (
                      <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid={`import-log-entry-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#A3B5C7]">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                          </span>
                          <span className="text-xs text-[#00D4C8]">{entry.author}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(entry.changes || []).map((change, j) => (
                            <span key={j} className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#00D4C8]/10 text-[#00D4C8] rounded border border-[#00D4C8]/20">
                              {change.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {entry.notes_count > 0 && (
                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-white/10 text-[#A3B5C7] rounded">
                              {entry.notes_count} note{entry.notes_count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    )) : <EmptyPlaceholder />}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="delete-confirm-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-white">Delete Target</h3>
            </div>
            <p className="text-[#D4E2F0] text-sm leading-relaxed mb-6">
              Are you sure you want to delete <span className="text-white font-medium">{target.name}</span>? This will also remove them from your pipeline and cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary text-sm"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTarget}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-2"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Target
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Briefing Modal */}
      {showBriefing && target && (
        <BriefingModal
          targetId={id}
          targetName={target.name}
          onClose={() => setShowBriefing(false)}
        />
      )}
    </div>
  );
};

export default TargetDetailPage;
